/**
 * Conversion entre les structures historiques de l'interface et les DTO de l'API.
 *
 * L'interface manipule des objets hérités du prototype (`adr`, `npa`, lignes en
 * `d`/`u`/`q`/`p`/`rem`) tandis que le backend expose des noms explicites. Ces
 * fonctions isolent la traduction pour que ni l'un ni l'autre n'ait à changer.
 */
import { adresseApi } from './client';
import { echeanceJours } from '../lib/dates';
import { libelleDelai } from '../lib/echeances';
import { paliersValides } from '../lib/paliers';
import { idDefinitif } from '../lib/identifiants';
import { membresSansCode } from '../lib/personnel';
import { heuresParSemaine } from '../lib/pointage';
import { localeDates } from '../lib/i18n';
import { coordonnee, joursCsv, listeCsv } from '../lib/lieux-travail';

const nombre = (valeur) => {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  // Référence à un enregistrement créé pendant la session : son numéro
  // attribué par le serveur, plutôt que rien.
  const n = Number(idDefinitif(valeur));
  return Number.isFinite(n) ? n : null;
};

/** Convertit une date `JJ.MM.AAAA` en `AAAA-MM-JJ`, en laissant passer l'ISO. */
export function dateVersIso(valeur) {
  if (!valeur) return null;
  const texte = String(valeur).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(texte)) return texte;

  const m = texte.match(/^(\d{1,2})[./](\d{1,2})[./](\d{2,4})$/);
  if (!m) return null;

  const annee = m[3].length === 2 ? `20${m[3]}` : m[3];
  return `${annee}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}

/** Convertit une date ISO `AAAA-MM-JJ` en `JJ.MM.AAAA` pour l'affichage. */
export function isoVersDate(valeur) {
  if (!valeur) return '';
  const m = String(valeur).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(valeur);
}

/** Interface → API : client. */
export function clientVersDto(client) {
  return {
    // Un identifiant qui n'est pas un nombre est provisoire : l'ecran vient
    // de le fabriquer, le serveur ne le connait pas, et le lui envoyer rend la
    // requete illisible — son contrat attend un nombre.
    id: typeof client.id === 'number' ? client.id : null,
    nom: client.nom ?? '',
    adresse: client.adr ?? client.adresse ?? null,
    npa: client.npa ?? null,
    telephone: client.tel ?? client.telephone ?? null,
    email: client.email ?? null,
    contact: client.contact ?? null,
    remiseGlobale: nombre(client.remiseGlobale) ?? 0,
    notes: client.notes ?? null,
    remises: client.remises ?? {},
    // Le dossier d'ouverture de compte : identite, interlocuteur, acheminement
    // des factures, etat du compte et suivi. Absents de la charge utile, ces
    // renseignements se saisissaient a l'ecran sans jamais atteindre la base.
    formeJuridique: client.forme || null,
    numeroIde: client.ide || client.tva || null,
    profession: client.profession || null,
    nombreEmployes: client.nbEmployes ? String(client.nbEmployes) : null,
    rue: client.rue || null,
    casePostale: client.casePostale || null,
    contactFonction: client.fonction || null,
    mobile: client.mobile || null,
    emailFactures: client.emailFactures || null,
    emailCorrespondance: client.emailCorr || null,
    telephoneFacturation: client.telFacturation || null,
    accepteFactureElectronique: client.accepteElec === true,
    compteStatut: client.compteStatut || null,
    dateDemande: dateVersIso(client.dateDemande),
    dateOuverture: dateVersIso(client.dateOuverture),
    signature: client.signature || null,
    crmStatut: client.crmStatut || null,
    relanceLe: dateVersIso(client.relanceLe),
    suivi: client.suivi || null,
    // L'historique daté des échanges, envoyé entier : le serveur le remplace,
    // suppressions comprises. Une entrée sans date lisible ou vide ne part pas.
    historique: (client.histo || [])
      .map((h) => ({ date: dateVersIso(h.date), texte: String(h.texte ?? '').trim() }))
      .filter((h) => h.date && h.texte),
    delaiPaiementJours: nombre(client.delaiPaiement) ?? null,
    // Les points etaient credites a chaque vente et perdus aussitot.
    points: nombre(client.points) ?? 0,
    // Les conditions internes du compte (magasin) : representant, conditions
    // groupe, limite de credit, conditions de paiement. Imprimees sur la fiche
    // d'ouverture A4, elles ne quittaient pas l'ecran. Seules les conditions
    // renseignees partent.
    conditions: Object.fromEntries(
      Object.entries(client.cond || {})
        .map(([k, v]) => [k, String(v ?? '').trim()])
        .filter(([, v]) => v),
    ),
    // Les personnes à contacter de la société (directeur, techniciens…), qui
    // reçoivent le catalogue. Absente de la fiche, la liste n'est pas envoyée :
    // le serveur garde celle qu'il a.
    contacts: Array.isArray(client.contacts)
      ? client.contacts
          .map((c) => ({
            nom: String(c?.nom ?? '').trim(),
            email: String(c?.email ?? '').trim() || null,
            fonction: String(c?.fonction ?? '').trim() || null,
          }))
          .filter((c) => c.nom)
      : null,
  };
}

/** API → interface : client. */
export function dtoVersClient(dto) {
  return {
    id: dto.id,
    nom: dto.nom,
    adr: dto.adresse ?? '',
    npa: dto.npa ?? '',
    tel: dto.telephone ?? '',
    email: dto.email ?? '',
    contact: dto.contact ?? '',
    remiseGlobale: dto.remiseGlobale ?? 0,
    notes: dto.notes ?? '',
    remises: dto.remises ?? {},
    forme: dto.formeJuridique ?? '',
    ide: dto.numeroIde ?? '',
    tva: dto.numeroIde ?? '',
    profession: dto.profession ?? '',
    nbEmployes: dto.nombreEmployes ?? '',
    rue: dto.rue ?? '',
    casePostale: dto.casePostale ?? '',
    fonction: dto.contactFonction ?? '',
    mobile: dto.mobile ?? '',
    emailFactures: dto.emailFactures ?? '',
    emailCorr: dto.emailCorrespondance ?? '',
    telFacturation: dto.telephoneFacturation ?? '',
    accepteElec: dto.accepteFactureElectronique === true,
    compteStatut: dto.compteStatut ?? '',
    dateDemande: dto.dateDemande ?? '',
    dateOuverture: dto.dateOuverture ?? '',
    signature: dto.signature ?? '',
    // Les extraits vivent hors de la base : la fiche n'en porte que le nom, le
    // contenu se demande au point d'entree des pieces.
    docRCNom: dto.extraitRcNom ?? '',
    docRC: dto.extraitRcCle ? adresseApi(`/clients/${dto.id}/pieces/REGISTRE_COMMERCE`) : '',
    docPoursuiteNom: dto.extraitPoursuitesNom ?? '',
    docPoursuite: dto.extraitPoursuitesCle
      ? adresseApi(`/clients/${dto.id}/pieces/POURSUITES`)
      : '',
    crmStatut: dto.crmStatut ?? '',
    relanceLe: dto.relanceLe ?? '',
    suivi: dto.suivi ?? '',
    histo: (dto.historique || []).map((h) => ({ date: isoVersDate(h.date), texte: h.texte })),
    // Vide, c'est le delai par defaut de l'entreprise qui s'applique.
    delaiPaiement: dto.delaiPaiementJours ?? '',
    points: dto.points ?? 0,
    // La fiche d'ouverture A4 et le formulaire lisent `cond`.
    cond: { ...(dto.conditions || {}) },
    contacts: (dto.contacts || []).map((c) => ({
      nom: c.nom || '',
      email: c.email || '',
      fonction: c.fonction || '',
    })),
  };
}

/** Interface → API : ligne de document. */
export function ligneVersDto(ligne, position) {
  return {
    id: typeof ligne.id === 'number' ? ligne.id : null,
    position,
    designation: ligne.d ?? ligne.designation ?? '',
    sousDesignation: ligne.sd ?? ligne.sousDesignation ?? null,
    unite: ligne.u ?? ligne.unite ?? null,
    quantite: nombre(ligne.q) ?? 0,
    prixUnitaire: nombre(ligne.p) ?? 0,
    remise: nombre(ligne.rem) ?? 0,
    tauxTva: nombre(ligne.tva),
    taxable: ligne.taxe !== false,
    referenceArticle: ligne.ref ?? null,
    miseEnAvant: ligne.hl ?? null,
    // Cle stable attribuee par le serveur : les photos et plans de la ligne
    // s'y rattachent, et la perdre les detacherait au prochain enregistrement.
    cle: ligne.cle ?? null,
    // Tarifs degressifs : saisis a l'ecran, ils n'etaient jamais enregistres.
    // Seuls les paliers complets partent ; sans palier, rien.
    paliers: paliersVersDto(ligne.paliers),
  };
}

/** Interface → API : paliers d'une ligne, ou null s'il n'y en a aucun. */
function paliersVersDto(paliers) {
  const valides = paliersValides(paliers);
  return valides.length ? valides.map((x) => ({ quantite: x.q, prixUnitaire: x.p })) : null;
}

/** API → interface : ligne de document. */
export function dtoVersLigne(dto) {
  return {
    id: dto.id,
    d: dto.designation ?? '',
    sd: dto.sousDesignation ?? '',
    u: dto.unite ?? '',
    q: dto.quantite ?? 0,
    p: dto.prixUnitaire ?? 0,
    rem: dto.remise ?? 0,
    tva: dto.tauxTva,
    taxe: dto.taxable !== false,
    ref: dto.referenceArticle ?? undefined,
    hl: dto.miseEnAvant ?? undefined,
    cle: dto.cle ?? undefined,
    // Les paliers reviennent sous la forme que l'editeur manipule ({q, p}).
    // Absents, la cle ne figure pas : la ligne garde la forme d'avant.
    ...(dto.paliers && dto.paliers.length
      ? { paliers: dto.paliers.map((x) => ({ q: x.quantite, p: x.prixUnitaire })) }
      : {}),
  };
}

/**
 * Pièce de ligne reçue du serveur → média affiché sur la ligne.
 *
 * @param {object} piece pièce jointe telle que l'API la décrit
 * @returns {{id: number, data: string, type: 'Photo'|'Plan', pdf: boolean, nom: string}} le média
 */
export function mediaDeLigne(piece) {
  return {
    id: piece.id,
    data: piece.fichier ? adresseApi(piece.fichier) : '',
    type: piece.typePiece === 'PLAN' ? 'Plan' : 'Photo',
    pdf: piece.typeMedia === 'application/pdf',
    nom: piece.libelle || '',
  };
}

// L'ecran emploie ce vocabulaire-la. Il n'y avait aucune table : le mot
// repartait en majuscules, « Accepte » devenait « ACCEPTÉ » — accent compris —
// et « En attente » devenait « EN ATTENTE ». L'enumeration du serveur n'en
// connait aucun, et accepter un devis se soldait par une panne au lieu d'un
// enregistrement.
const STATUTS_DEVIS = {
  Brouillon: 'BROUILLON',
  'En attente': 'ENVOYE',
  Envoyé: 'ENVOYE',
  Envoyée: 'ENVOYE',
  Accepté: 'ACCEPTE',
  Refusé: 'REFUSE',
  Expiré: 'EXPIRE',
};

/** API → interface : les deux mots du serveur que l'ecran distingue. */
const STATUTS_DEVIS_LECTURE = {
  BROUILLON: 'Brouillon',
  ENVOYE: 'En attente',
  ACCEPTE: 'Accepté',
  REFUSE: 'Refusé',
  EXPIRE: 'Expiré',
};

/**
 * Interface → API : statut de devis.
 *
 * @param {string} statut statut tel que l'écran l'écrit
 * @returns {string} le statut attendu par le serveur
 */
export function statutDevisVersApi(statut) {
  return STATUTS_DEVIS[statut] ?? String(statut ?? '').toUpperCase();
}

/**
 * API → interface : statut de devis.
 *
 * @param {string} statut statut rendu par le serveur
 * @returns {string} le statut tel que l'écran l'écrit
 */
export function statutDevisVersInterface(statut) {
  return STATUTS_DEVIS_LECTURE[statut] ?? statut;
}

/** Interface → API : devis. */
export function devisVersDto(devis) {
  return {
    id: typeof devis.id === 'number' ? devis.id : null,
    numero: devis.num ?? (typeof devis.id === 'string' ? devis.id : null),
    clientId: devis.clientId ?? null,
    siteId: nombre(devis.siteId),
    dateDocument: dateVersIso(devis.date),
    statut: devis.statut ? statutDevisVersApi(devis.statut) : null,
    modele: devis.modeleId ?? null,
    style: devis.style ?? null,
    couleurDocument: devis.docCouleur ?? null,
    description: devis.desc ?? null,
    introduction: devis.intro ?? null,
    mentions: devis.mentions ?? null,
    messagePied: devis.piedMessage || null,
    // Couleur et gras des deux messages : « haut:rouge:g;bas:bleu ».
    styleMessages: devis.stylesMessages || null,
    delaiExecution: devis.delai ?? null,
    validiteJours: nombre(devis.validite) ?? 30,
    tauxTva: nombre(devis.tvaPct),
    remiseGlobale: nombre(devis.remisePct) ?? 0,
    // L'ecran ecrit « acompte » ; ne lire que « acomptePct » perdait l'acompte
    // a chaque enregistrement, et aucune facture d'acompte n'etait possible.
    acomptePourcentage: nombre(devis.acompte ?? devis.acomptePct) ?? 0,
    // L'acompte écrit en francs (TVA comprise) : il fait foi ; vide, l'acompte
    // est en pourcentage.
    acompteMontant: nombre(devis.acompteMontant) > 0 ? nombre(devis.acompteMontant) : null,
    acompteFacture: devis.acompteFacture ?? null,
    // Le vehicule d'une offre de garage : saisi et imprime, il n'etait pas
    // envoye, et disparaissait au rechargement.
    plaque: devis.plaque || null,
    vehicule: devis.vehicule || null,
    // Le numero du bon de commande du client, imprime sur le document.
    commandeClient: String(devis.commandeClient ?? '').trim() || null,
    // Les autres adresses qui recoivent l'offre par e-mail avec la fiche (le
    // patron, le technicien…). Absentes de l'offre, elles ne sont pas
    // envoyees : le serveur garde les siennes.
    emailsEnvoi: Array.isArray(devis.emailsEnvoi)
      ? devis.emailsEnvoi.map((e) => String(e ?? '').trim()).filter(Boolean)
      : null,
    bonCommande: devis.bonCommande === true,
    tampon: devis.tampon === true,
    sansPaiement: devis.noPay === true,
    dateSignature: dateVersIso(devis.signDate),
    signataire: devis.signePar ?? null,
    lignes: (devis.lignes ?? []).map(ligneVersDto),
    secteur: devis.sect ?? null,
    relanceLe: dateVersIso(devis.relanceLe),
  };
}

/** API → interface : devis. */
export function dtoVersDevis(dto) {
  return {
    id: dto.id,
    num: dto.numero,
    clientId: dto.clientId,
    siteId: dto.siteId,
    date: isoVersDate(dto.dateDocument),
    statut: statutDevisVersInterface(dto.statut),
    modeleId: dto.modele,
    style: dto.style,
    docCouleur: dto.couleurDocument,
    desc: dto.description ?? '',
    intro: dto.introduction ?? '',
    mentions: dto.mentions ?? '',
    piedMessage: dto.messagePied ?? '',
    stylesMessages: dto.styleMessages ?? '',
    delai: dto.delaiExecution ?? '',
    validite: dto.validiteJours,
    tvaPct: dto.tauxTva,
    remisePct: dto.remiseGlobale ?? 0,
    acomptePct: dto.acomptePourcentage ?? 0,
    acompte: dto.acomptePourcentage ?? 0,
    acompteMontant: dto.acompteMontant ?? null,
    acompteEnFrancs: dto.acompteMontant != null,
    acompteFacture: dto.acompteFacture ?? null,
    // Le montant de l'acompte et l'identifiant de sa reference de paiement,
    // calcules par le serveur : le QR de l'offre a l'ecran porte les memes
    // que le PDF et que la facture d'acompte.
    montantAcompte: dto.montantAcompte ?? null,
    identifiantReference: dto.identifiantReference ?? null,
    plaque: dto.plaque ?? '',
    vehicule: dto.vehicule ?? '',
    commandeClient: dto.commandeClient ?? '',
    emailsEnvoi: [...(dto.emailsEnvoi || [])],
    bonCommande: dto.bonCommande === true,
    tampon: dto.tampon === true,
    noPay: dto.sansPaiement === true,
    signDate: isoVersDate(dto.dateSignature),
    signePar: dto.signataire ?? '',
    // La signature tracée par le client et l'instant où il l'a donnée, lus
    // seulement : c'est le serveur qui les pose à l'acceptation.
    sig: dto.signature ?? '',
    signeLe: dto.signeLe ?? null,
    // Le refus du client, par son lien ou sur la tablette : la date et le
    // motif qu'il a donné, lus seulement — c'est le serveur qui les pose.
    refuseLe: isoVersDate(dto.refuseLe),
    motifRefus: dto.motifRefus ?? '',
    expiration: isoVersDate(dto.dateExpiration),
    // Les photos et plans d'une ligne arrivent parmi les pieces du devis,
    // marques de la cle de leur ligne : ils regagnent ici leur ligne.
    lignes: (dto.lignes ?? []).map((l) => ({
      ...dtoVersLigne(l),
      imgs: (dto.pieces ?? [])
        .filter((pc) => pc.ligneCle && pc.ligneCle === l.cle)
        .map(mediaDeLigne),
    })),
    totaux: dto.totaux ?? null,
    sect: dto.secteur ?? undefined,
    relanceLe: isoVersDate(dto.relanceLe),
    // Les pieces accompagnent le devis en lecture : elles s'ajoutent et se
    // retirent par leurs propres appels, et ne repartent donc pas dans
    // `devisVersDto`.
    pieces: (dto.pieces ?? [])
      .filter((pc) => !pc.ligneCle)
      .map((pc) => ({
        id: pc.id,
        fournisseur: pc.fournisseur ?? '',
        ref: pc.reference ?? '',
        montant: pc.montant ?? 0,
        cat: pc.categorie ?? '',
        date: isoVersDate(pc.date),
        photo: pc.fichier ? adresseApi(pc.fichier) : '',
      })),
  };
}

/** Interface → API : facture. */
export function factureVersDto(facture) {
  return {
    id: typeof facture.id === 'number' ? facture.id : null,
    numero: facture.num ?? (typeof facture.id === 'string' ? facture.id : null),
    clientId: facture.clientId ?? null,
    siteId: nombre(facture.siteId),
    quoteId: nombre(facture.devisId),
    typeFacture: facture.type ?? 'standard',
    dateDocument: dateVersIso(facture.date),
    delaiPaiementJours: delaiEnJours(facture.echeance),
    statut: facture.statut ? statutFactureVersApi(facture.statut) : null,
    datePaiement: dateVersIso(facture.paidDate ?? facture.datePaiement),
    dateCompta: dateVersIso(facture.dateCompta),
    // Encaissements partiels et rappels : le rapprochement bancaire et la page
    // des factures les etablissent, le rappel A4 les imprime. Sans eux dans la
    // charge utile, ils repartaient a zero au premier rechargement.
    montantPaye: nombre(facture.paidAmount) ?? 0,
    rappelNiveau: nombre(facture.rappelNiveau) ?? 0,
    rappelLe: dateVersIso(facture.rappelLe),
    rappelFrais: nombre(facture.rappelFrais) ?? 0,
    rappelAutoLe: dateVersIso(facture.rappelAutoLe),
    tauxTva: nombre(facture.tvaPct),
    remiseGlobale: nombre(facture.remisePct) ?? 0,
    // L'acompte demandé sur la facture : en francs, le montant fait foi.
    acomptePourcentage:
      +facture.acompteDemandeMontant > 0 ? null : nombre(facture.acompteDemande) || null,
    acompteMontant:
      +facture.acompteDemandeMontant > 0 ? nombre(facture.acompteDemandeMontant) : null,
    mentions: facture.mentions ?? null,
    messagePied: facture.piedMessage || null,
    styleMessages: facture.stylesMessages || null,
    // Le message au client en haut de la facture : il se perdait à l'enregistrement.
    introduction: facture.intro || null,
    // Sur un avoir : la facture qu'il crédite.
    avoirDe: facture.type === 'avoir' ? nombre(facture.avoirDe) : null,
    // L'objet de la facture, repris de l'offre : il se perdait au rechargement.
    description: String(facture.desc || '').trim() || null,
    style: facture.style ?? null,
    couleurDocument: facture.docCouleur ?? null,
    // Les colonnes existaient, le mappeur ne les remplissait pas : le tampon
    // et la mention « sans paiement » disparaissaient au rechargement, et le
    // document revenait autre qu'il n'avait ete compose.
    tampon: facture.tampon === true,
    sansPaiement: facture.noPay === true,
    // Le dossier ne disait plus si la piece etait partie, ce qui est
    // precisement ce qu'on regarde avant de relancer.
    envoyeLe: dateVersIso(facture.envoyeLe),
    vuLe: dateVersIso(facture.vuLe),
    // Une facture d'entretien mensuelle cessait de se reconduire des le
    // premier rechargement, et l'oubli ne se voit que le mois suivant.
    recurFreq: facture.recur?.freq ?? null,
    // Un contrat d'entretien se suspend pour l'hiver : la pause ne survivait
    // pas au rechargement, et la facture repartait le mois suivant.
    // Le serveur normalise l'absence de recurrence en « actif » : envoyer
    // null ici faisait juger chaque facture modifiee, et la salve reecrivait
    // toutes les factures a chaque action — jusqu'a buter sur une facture
    // payee, qui refuse toute ecriture.
    recurActif: facture.recur ? facture.recur.actif !== false : true,
    recurProchaine: dateVersIso(facture.recur?.prochaine),
    recurDe: facture.recurDe ?? null,
    // En mode mixte, le document perdait son univers : une facture de magasin
    // revenait en facture de chantier, vocabulaire et mise en page compris.
    secteur: facture.sect ?? null,
    relanceLe: dateVersIso(facture.relanceLe),
    rembMontant: nombre(facture.remboursement?.montant),
    rembIban: facture.remboursement?.iban || null,
    rembMotif: facture.remboursement?.motif || null,
    rembDate: dateVersIso(facture.remboursement?.date),
    // Le vehicule repris de l'offre de garage.
    plaque: facture.plaque || null,
    vehicule: facture.vehicule || null,
    commandeClient: String(facture.commandeClient ?? '').trim() || null,
    // Les autres adresses qui recoivent la facture et ses rappels, reprises de
    // l'offre. Absentes, elles ne sont pas envoyees : le serveur garde les siennes.
    emailsEnvoi: Array.isArray(facture.emailsEnvoi)
      ? facture.emailsEnvoi.map((e) => String(e ?? '').trim()).filter(Boolean)
      : null,
    lignes: (facture.lignes ?? []).map(ligneVersDto),
  };
}

/** API → interface : facture. */
export function dtoVersFacture(dto) {
  return {
    id: dto.id,
    num: dto.numero,
    clientId: dto.clientId,
    siteId: dto.siteId,
    devisId: dto.quoteId,
    type: dto.typeFacture,
    // La facture d'acompte d'une offre porte la reference du QR de l'offre,
    // non celle de son numero : c'est le serveur qui le dit.
    identifiantReference: dto.identifiantReference ?? null,
    plaque: dto.plaque ?? '',
    vehicule: dto.vehicule ?? '',
    commandeClient: dto.commandeClient ?? '',
    emailsEnvoi: [...(dto.emailsEnvoi || [])],
    date: isoVersDate(dto.dateDocument),
    echeance: dto.delaiPaiementJours == null ? '30 jours' : libelleDelai(dto.delaiPaiementJours),
    dateEcheance: isoVersDate(dto.dateEcheance),
    joursRetard: dto.joursRetard ?? 0,
    statut: statutFactureVersInterface(dto.statut),
    paidDate: isoVersDate(dto.datePaiement),
    dateCompta: isoVersDate(dto.dateCompta),
    paidAmount: dto.montantPaye ?? 0,
    rappelNiveau: dto.rappelNiveau ?? 0,
    rappelLe: isoVersDate(dto.rappelLe),
    rappelFrais: dto.rappelFrais ?? 0,
    rappelAutoLe: isoVersDate(dto.rappelAutoLe),
    tvaPct: dto.tauxTva,
    remisePct: dto.remiseGlobale ?? 0,
    acompteDemande: dto.acomptePourcentage ?? 0,
    acompteDemandeMontant: dto.acompteMontant ?? null,
    acompteDemandeEnFrancs: dto.acompteMontant != null,
    mentions: dto.mentions ?? '',
    piedMessage: dto.messagePied ?? '',
    stylesMessages: dto.styleMessages ?? '',
    intro: dto.introduction ?? '',
    avoirDe: dto.avoirDe ?? null,
    desc: dto.description ?? '',
    style: dto.style,
    docCouleur: dto.couleurDocument,
    lignes: (dto.lignes ?? []).map(dtoVersLigne),
    totaux: dto.totaux ?? null,
    tampon: dto.tampon === true,
    noPay: dto.sansPaiement === true,
    envoyeLe: isoVersDate(dto.envoyeLe),
    envoye: !!dto.envoyeLe,
    vuLe: isoVersDate(dto.vuLe),
    vu: !!dto.vuLe,
    recur: dto.recurFreq
      ? {
          freq: dto.recurFreq,
          prochaine: isoVersDate(dto.recurProchaine),
          actif: dto.recurActif !== false,
        }
      : null,
    recurDe: dto.recurDe ?? undefined,
    sect: dto.secteur ?? undefined,
    relanceLe: isoVersDate(dto.relanceLe),
    remboursement:
      dto.rembMontant == null
        ? undefined
        : {
            montant: dto.rembMontant,
            iban: dto.rembIban ?? '',
            motif: dto.rembMotif ?? '',
            date: isoVersDate(dto.rembDate),
          },
  };
}

/**
 * Extrait le nombre de jours d'un libellé d'échéance (« 30 jours »,
 * « À réception » pour 0).
 */
export function delaiEnJours(echeance) {
  return echeanceJours(echeance ?? '');
}

const STATUTS_FACTURE = {
  Brouillon: 'BROUILLON',
  Envoyée: 'ENVOYEE',
  Payée: 'PAYEE',
  // Absent de cette table, l'etat partiel repartait tel quel vers l'API, qui
  // ne le connaissait pas : l'enregistrement se soldait par une panne.
  Partielle: 'PARTIELLE',
  'En retard': 'EN_RETARD',
  Annulée: 'ANNULEE',
};

/** Interface → API : statut de facture. */
export function statutFactureVersApi(statut) {
  return STATUTS_FACTURE[statut] ?? String(statut).toUpperCase();
}

/** API → interface : statut de facture. */
export function statutFactureVersInterface(statut) {
  const entree = Object.entries(STATUTS_FACTURE).find(([, api]) => api === statut);
  return entree ? entree[0] : statut;
}

/** Interface → API : chantier. */
export function chantierVersDto(chantier) {
  return {
    id: typeof chantier.id === 'number' ? chantier.id : null,
    code: chantier.code ?? (typeof chantier.id === 'string' ? chantier.id : ''),
    nom: chantier.nom ?? '',
    clientId: chantier.clientId ?? null,
    adresse: chantier.adr ?? null,
    npa: chantier.npa ?? null,
    statut: statutChantierVersApi(chantier.statut),
    avancement: nombre(chantier.av) ?? 0,
    montantFacture: nombre(chantier.facture) ?? 0,
    gpsLatitude: nombre(chantier.gps?.lat),
    gpsLongitude: nombre(chantier.gps?.lng),
    // Dates de la frise du planning. Absentes de la charge utile, elles
    // etaient remises a nul en base a chaque synchronisation.
    dateDebut: dateVersIso(chantier.debut),
    dateFin: dateVersIso(chantier.fin),
    notes: chantier.notes ?? null,
    // Postes de travail, repris des lignes du devis a la creation : ils
    // classent les photos (« repris de l'offre »). Non transmis, seul
    // « General » restait propose apres rechargement.
    postes: (chantier.postes ?? []).map((p) => String(p ?? '').trim()).filter(Boolean),
    // Devis accepte dont le chantier est issu. Un devis signe ne s'ecrit
    // plus : c'est le chantier qui porte le lien. Nul, il laisse en base le
    // lien deja enregistre.
    devisId: nombre(chantier.devisId),
  };
}

/** API → interface : chantier. */
export function dtoVersChantier(dto) {
  return {
    id: dto.id,
    code: dto.code,
    nom: dto.nom,
    clientId: dto.clientId,
    adr: dto.adresse ?? '',
    npa: dto.npa ?? '',
    statut: statutChantierVersInterface(dto.statut),
    av: dto.avancement ?? 0,
    facture: dto.montantFacture ?? 0,
    gps:
      dto.gpsLatitude != null && dto.gpsLongitude != null
        ? { lat: dto.gpsLatitude, lng: dto.gpsLongitude }
        : null,
    // La frise du planning lit et ecrit ces dates en ISO (champs « date »).
    debut: dto.dateDebut ?? '',
    fin: dto.dateFin ?? '',
    notes: dto.notes ?? '',
    postes: dto.postes ?? [],
    devisId: dto.devisId ?? null,
  };
}

const STATUTS_CHANTIER = {
  Planifié: 'PLANIFIE',
  'En cours': 'EN_COURS',
  Suspendu: 'SUSPENDU',
  Terminé: 'TERMINE',
};

/** Interface → API : statut de chantier. */
export function statutChantierVersApi(statut) {
  return STATUTS_CHANTIER[statut] ?? (statut ? String(statut).toUpperCase() : null);
}

/** API → interface : statut de chantier. */
export function statutChantierVersInterface(statut) {
  const entree = Object.entries(STATUTS_CHANTIER).find(([, api]) => api === statut);
  return entree ? entree[0] : statut;
}

/** Interface → API : employé. */
export function employeVersDto(employe) {
  return {
    id: typeof employe.id === 'number' ? employe.id : null,
    nom: employe.nom ?? '',
    prenom: employe.prenom ?? null,
    fonction: employe.role ?? employe.fonction ?? null,
    adresse: employe.adresse ?? null,
    npa: employe.npa ?? null,
    ville: employe.ville ?? null,
    numeroAvs: employe.avs ?? null,
    dateEmbauche: dateVersIso(employe.dateEmbauche),
    dateNaissance: dateVersIso(employe.dateNaissance),
    telephone: employe.tel ?? null,
    email: employe.email ?? null,
    iban: employe.iban ?? null,
    tauxHoraire: nombre(employe.txH) ?? 0,
    salaireBase: nombre(employe.salBase) ?? 0,
    joursConges: nombre(employe.conges) ?? 20,
    tauxLpp: nombre(employe.lpp),
    tauxImpotSource: nombre(employe.is) ?? 0,
    heuresJour: nombre(employe.heuresJour) ?? 8,
    // L'horaire du contrat de chacun : arrivée, début et durée de la pause.
    heureDebut: employe.heureDebut || null,
    pauseDebut: employe.pauseDebut || null,
    pauseMinutes: nombre(employe.pauseMin),
    // L'heure de fin et la pause payée du matin : la journée s'en déduit.
    heureFin: employe.heureFin || null,
    pausePayeeDebut: employe.pausePayeeDebut || null,
    pausePayeeMinutes: nombre(employe.pausePayeeMin),
    treiziemeSalaire: employe.sal13 === true,
    couleur: employe.col ?? null,
    // Le dossier du personnel : conditions d'engagement, qualification, sejour,
    // versement, depart. Absents de la charge utile, ces champs se saisissaient
    // a l'ecran sans jamais atteindre la base, et le contrat comme la lettre de
    // demission se composaient de donnees qui disparaissaient au rechargement.
    typeContrat: employe.typeContrat || null,
    tauxOccupation: nombre(employe.taux),
    // La semaine se déduit de la journée du contrat et du taux.
    heuresSemaine: heuresParSemaine(employe.heuresJour, employe.taux),
    moisEssai: employe.essai || null,
    delaiResiliation: employe.delaiResil || null,
    dateFin: dateVersIso(employe.dateFin),
    lieuTravail: employe.lieu || null,
    responsable: employe.responsable || null,
    // Vide, la chaîne part telle quelle : le serveur efface alors les clauses.
    clausesContrat: employe.clausesContrat ?? null,
    // Où il pointe. Une chaîne vide remet « comme avant » (chantier, pas de
    // télétravail) ; absent (null), le serveur garde ce qui est enregistré.
    lieuHabituel: employe.lieuHabituel ?? null,
    lieuxAutres: employe.lieuxAutres == null ? null : listeCsv(employe.lieuxAutres).join(','),
    teletravail: employe.teletravail ?? null,
    teletravailJours:
      employe.teletravailJours == null ? null : joursCsv(employe.teletravailJours).join(','),
    metier: employe.metier || null,
    cfc: employe.cfc || null,
    anneesExperience: nombre(employe.exp),
    cct: employe.cct || null,
    classeCct: employe.classe || null,
    nationalite: employe.nationalite || null,
    permisNumero: employe.permisNum || null,
    permisExpireLe: dateVersIso(employe.permisExp),
    canton: employe.canton || null,
    soumisImpotSource: employe.soumisIS === undefined ? null : employe.soumisIS !== false,
    // Identité complémentaire et assurances : saisis dans l'éditeur, ils ne
    // partaient pas au serveur et disparaissaient au rechargement. Un
    // interrupteur jamais touché part vide : le serveur le lit « soumis ».
    sexe: employe.sexe || null,
    etatCivil: employe.etatCivil || null,
    langue: employe.langue || null,
    permisType: employe.permis || null,
    couvertureLaa: employe.laa || null,
    soumisAvs: employe.soumisAVS === undefined ? null : employe.soumisAVS !== false,
    soumisAc: employe.soumisAC === undefined ? null : employe.soumisAC !== false,
    soumisLaa: employe.soumisLAA === undefined ? null : employe.soumisLAA !== false,
    soumisLpp: employe.soumisLPP === undefined ? null : employe.soumisLPP !== false,
    banque: employe.banque || null,
    titulaireCompte: employe.titulaire || null,
    tauxVacances: nombre(employe.tauxVac),
    soldeVacancesInitial: nombre(employe.soldeInit),
    fraisRepas: nombre(employe.fraisRepas),
    parti: employe.parti === true,
    dateDepart: dateVersIso(employe.dateDepart),
    motifDepart: employe.motifDepart || null,
    formationNom: employe.formation?.nom || null,
    formationCout: nombre(employe.formation?.cout),
    formationEngagementMois: nombre(employe.formation?.engagementMois),
    formationDebut: dateVersIso(employe.formation?.debut),
    formationMensualite: nombre(employe.formation?.mensualite),
    formationTexte: employe.formation?.texte || null,
    enfants: employe.enfantsList ? employe.enfantsList.map(enfantVersDto) : undefined,
    documents: employe.documents ? employe.documents.map(pieceVersDto) : undefined,
    gpsActif: employe.gps?.actif === true,
    // La position ne se transmet qu'entière : une latitude sans horodatage ne
    // dit pas si l'employé est là maintenant ou y était la semaine passée.
    gpsLatitude: nombre(employe.gps?.lat) ?? null,
    gpsLongitude: nombre(employe.gps?.lng) ?? null,
    gpsMisAJourLe: employe.gps?.maj ?? null,
    // Un chantier encore local porte un identifiant de travail, que la base
    // refuserait : seul un identifiant venu du serveur est transmis.
    gpsSiteId: typeof employe.gps?.chId === 'number' ? employe.gps.chId : null,
    signature: employe.signature || null,
    // Les pointages sur chantier appartiennent au chantier : ils sont renvoyés
    // tels quels, le serveur ne réécrivant que ceux qui n'en relèvent pas.
    pointages: employe.badges ? employe.badges.map(badgeVersDto) : undefined,
    actif: employe.actif !== false,
    // Le code n'accompagne la fiche que lorsqu'il vient d'être saisi ou effacé :
    // le serveur ne le renvoie jamais, et l'envoyer vide à chaque
    // enregistrement retirerait l'accès au chantier.
    codePin: employe.codePin === undefined ? undefined : employe.codePin,
    // Le fil ne voyage plus avec la fiche : renvoyé en entier, il effaçait le
    // message que l'employé venait d'écrire. Chaque message part seul
    // (`employesApi.ecrit`), et le serveur ne réécrit plus le fil.
    messages: undefined,
  };
}

/** API → interface : employé. */
export function dtoVersEmploye(dto) {
  return {
    id: dto.id,
    nom: dto.nom,
    prenom: dto.prenom ?? '',
    role: dto.fonction ?? '',
    adresse: dto.adresse ?? '',
    npa: dto.npa ?? '',
    ville: dto.ville ?? '',
    avs: dto.numeroAvs ?? '',
    dateEmbauche: dto.dateEmbauche ?? '',
    // Absente de la relecture, la date de naissance repartait vide a chaque
    // enregistrement : la fiche perdait l'age, dont dependent le droit aux
    // vacances et le certificat de salaire.
    dateNaissance: dto.dateNaissance ?? '',
    tel: dto.telephone ?? '',
    email: dto.email ?? '',
    iban: dto.iban ?? '',
    txH: dto.tauxHoraire ?? 0,
    salBase: dto.salaireBase ?? 0,
    conges: dto.joursConges ?? 20,
    lpp: dto.tauxLpp,
    is: dto.tauxImpotSource ?? 0,
    heuresJour: dto.heuresJour ?? 8,
    heureDebut: dto.heureDebut ?? '07:00',
    pauseDebut: dto.pauseDebut ?? '12:00',
    pauseMin: dto.pauseMinutes ?? 60,
    heureFin: dto.heureFin ?? '',
    pausePayeeDebut: dto.pausePayeeDebut ?? '09:00',
    pausePayeeMin: dto.pausePayeeMinutes ?? 0,
    sal13: dto.treiziemeSalaire === true,
    col: dto.couleur ?? '',
    gps: {
      actif: dto.gpsActif === true,
      lat: dto.gpsLatitude == null ? undefined : Number(dto.gpsLatitude),
      lng: dto.gpsLongitude == null ? undefined : Number(dto.gpsLongitude),
      maj: dto.gpsMisAJourLe ?? null,
      at: heureLocale(dto.gpsMisAJourLe),
      chId: dto.gpsSiteId ?? undefined,
    },
    signature: dto.signature ?? '',
    badges: (dto.pointages ?? []).map(dtoVersBadge),
    typeContrat: dto.typeContrat ?? '',
    taux: dto.tauxOccupation ?? 100,
    hSem: dto.heuresSemaine ?? heuresParSemaine(dto.heuresJour, dto.tauxOccupation),
    essai: dto.moisEssai ?? '',
    delaiResil: dto.delaiResiliation ?? '',
    dateFin: dto.dateFin ?? '',
    lieu: dto.lieuTravail ?? '',
    responsable: dto.responsable ?? '',
    clausesContrat: dto.clausesContrat ?? '',
    ...lieuxVersInterface(dto),
    metier: dto.metier ?? '',
    cfc: dto.cfc ?? '',
    exp: dto.anneesExperience ?? 0,
    cct: dto.cct ?? '',
    classe: dto.classeCct ?? '',
    nationalite: dto.nationalite ?? '',
    permisNum: dto.permisNumero ?? '',
    permisExp: dto.permisExpireLe ?? '',
    canton: dto.canton ?? '',
    soumisIS: dto.soumisImpotSource !== false,
    sexe: dto.sexe ?? '',
    etatCivil: dto.etatCivil ?? '',
    langue: dto.langue ?? '',
    permis: dto.permisType ?? '',
    laa: dto.couvertureLaa ?? '',
    // Un assujettissement jamais réglé reste indéfini : l'éditeur l'affiche
    // « soumis », et il repart vide, sans réécrire la fiche à chaque relecture.
    soumisAVS: dto.soumisAvs ?? undefined,
    soumisAC: dto.soumisAc ?? undefined,
    soumisLAA: dto.soumisLaa ?? undefined,
    soumisLPP: dto.soumisLpp ?? undefined,
    // La fiche du patron d'une raison individuelle : ni chômage ni LPP, quoi
    // que disent ses cases (le serveur le sait, l'écran le suit).
    patronIndependant: dto.patronIndependant === true,
    banque: dto.banque ?? '',
    titulaire: dto.titulaireCompte ?? '',
    tauxVac: dto.tauxVacances ?? 0,
    soldeInit: dto.soldeVacancesInitial ?? 0,
    fraisRepas: dto.fraisRepas ?? 0,
    parti: dto.parti === true,
    dateDepart: dto.dateDepart ?? '',
    motifDepart: dto.motifDepart ?? '',
    formation: {
      nom: dto.formationNom ?? '',
      cout: dto.formationCout ?? 0,
      engagementMois: dto.formationEngagementMois ?? 0,
      debut: dto.formationDebut ?? '',
      // Une mensualite jamais saisie reste indefinie : la convention applique
      // alors son defaut de 500 CHF. A zero, elle disait « mensualites de
      // 0 CHF » et l'ecran affichait 0.
      mensualite: dto.formationMensualite ?? undefined,
      texte: dto.formationTexte ?? '',
    },
    enfantsList: (dto.enfants ?? []).map(dtoVersEnfant),
    documents: (dto.documents ?? []).map(dtoVersPiece),
    actif: dto.actif !== false,
    codePinDefini: dto.codePinDefini === true,
    messages: (dto.messages ?? []).map(dtoVersMessageEmploye),
  };
}

/** Heure locale d'un horodatage ISO, pour l'affichage d'une position. */
function heureLocale(instant) {
  if (!instant) return '';
  const d = new Date(instant);
  if (Number.isNaN(d.getTime())) return '';
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
}

/**
 * API → interface : fiche de l'employé connecté, vue depuis son espace.
 *
 * <p>C'est la fiche employé amputée de ce qui relève de la paie, que le serveur
 * ne lui sert pas. L'espace ouvrier la manipule comme les autres, d'où la même
 * forme : il lisait auparavant la liste complète du personnel.
 */
export function dtoVersMonEspace(dto) {
  return {
    id: dto.id,
    nom: dto.nom,
    prenom: dto.prenom ?? '',
    role: dto.fonction ?? '',
    dateEmbauche: dto.dateEmbauche ?? '',
    tel: dto.telephone ?? '',
    email: dto.email ?? '',
    conges: dto.joursConges ?? 20,
    heuresJour: dto.heuresJour ?? 8,
    heureDebut: dto.heureDebut ?? '07:00',
    pauseDebut: dto.pauseDebut ?? '12:00',
    pauseMin: dto.pauseMinutes ?? 60,
    heureFin: dto.heureFin ?? '',
    pausePayeeDebut: dto.pausePayeeDebut ?? '09:00',
    pausePayeeMin: dto.pausePayeeMinutes ?? 0,
    col: dto.couleur ?? '',
    gps: {
      actif: dto.gpsActif === true,
      lat: dto.gpsLatitude == null ? undefined : Number(dto.gpsLatitude),
      lng: dto.gpsLongitude == null ? undefined : Number(dto.gpsLongitude),
      maj: dto.gpsMisAJourLe ?? null,
      at: heureLocale(dto.gpsMisAJourLe),
      chId: dto.gpsSiteId ?? undefined,
    },
    signature: dto.signature ?? '',
    badges: (dto.pointages ?? []).map(dtoVersBadge),
    actif: true,
    // Le terme du contrat : l'espace annonce jusqu'à quand l'accès reste ouvert.
    dateFin: dto.dateFin ?? '',
    messages: (dto.messages ?? []).map(dtoVersMessageEmploye),
    // Les conditions de son propre contrat et sa convention de formation, en
    // lecture seule. Sans elles, l'onglet Contrat affichait CHF 0.00, 0/h et
    // « une durée indéterminée » même en CDD, et l'onglet Formation restait
    // vide. Elles portent les clés de la fiche d'équipe, que les écrans lisent.
    typeContrat: dto.typeContrat ?? '',
    essai: dto.moisEssai ?? '',
    delaiResil: dto.delaiResiliation ?? '',
    cct: dto.cct ?? '',
    classe: dto.classeCct ?? '',
    clausesContrat: dto.clausesContrat ?? '',
    lieu: dto.lieuTravail ?? '',
    taux: dto.tauxOccupation ?? null,
    // Où il peut pointer, réglé par le bureau : en lecture seule ici.
    ...lieuxVersInterface(dto),
    hSem: dto.heuresSemaine ?? null,
    salBase: dto.salaireBase ?? 0,
    txH: dto.tauxHoraire ?? 0,
    sal13: dto.treiziemeSalaire === true,
    // Les retenues qui s'appliquent à lui, sous les clés que lit l'estimation
    // (`lib/retenues`). Le serveur n'en sert que l'effet : la prime AANP
    // retenue ou non tient lieu d'interrupteur LAA. Sans ce bloc, il reste
    // soumis à tout, au taux LPP par défaut.
    ...cotisationsVersInterface(dto.cotisations),
    formation: {
      nom: dto.formationNom ?? '',
      cout: dto.formationCout ?? 0,
      engagementMois: dto.formationEngagementMois ?? 0,
      debut: dto.formationDebut ?? '',
      // Une mensualite jamais saisie reste indefinie : la convention applique
      // alors son defaut de 500 CHF. A zero, elle disait « mensualites de
      // 0 CHF » et l'ecran affichait 0.
      mensualite: dto.formationMensualite ?? undefined,
      texte: dto.formationTexte ?? '',
    },
  };
}

/** API → interface : lieu habituel, autres lieux et télétravail de l'employé. */
function lieuxVersInterface(dto) {
  return {
    lieuHabituel: dto.lieuHabituel ?? '',
    lieuxAutres: listeCsv(dto.lieuxAutres),
    teletravail: dto.teletravail ?? '',
    teletravailJours: joursCsv(dto.teletravailJours),
  };
}

/** API → interface : retenues de l'employé connecté, telles que sa fiche les calcule. */
function cotisationsVersInterface(cotisations) {
  if (!cotisations) return {};
  const non = (valeur) => (valeur === false ? false : undefined);
  // Les taux que sa fiche officielle applique, ceux que l'entreprise a réglés.
  // Un taux absent (assurance non soumise, serveur plus ancien) n'est pas
  // porté : l'estimation retombe alors sur le taux légal.
  const tauxRetenues = {};
  if (cotisations.tauxAvs != null) tauxRetenues.avs = cotisations.tauxAvs;
  if (cotisations.tauxAc != null) tauxRetenues.ac = cotisations.tauxAc;
  if (cotisations.tauxAanp != null) tauxRetenues.aanp = cotisations.tauxAanp;
  if (cotisations.tauxIjm != null) tauxRetenues.ijm = cotisations.tauxIjm;
  if (cotisations.tauxCanton != null) tauxRetenues.canton = cotisations.tauxCanton;
  return {
    soumisAVS: non(cotisations.avs),
    soumisAC: non(cotisations.ac),
    soumisLAA: non(cotisations.aanp),
    soumisLPP: non(cotisations.lpp),
    lpp: cotisations.tauxLpp ?? undefined,
    tauxRetenues,
  };
}

/** Interface → API : ce que l'employé saisit dans son espace. */
export function monEspaceVersDto(emp) {
  return {
    gpsActif: emp.gps?.actif === true,
    gpsLatitude: nombre(emp.gps?.lat) ?? null,
    gpsLongitude: nombre(emp.gps?.lng) ?? null,
    gpsMisAJourLe: emp.gps?.maj ?? null,
    gpsSiteId: typeof emp.gps?.chId === 'number' ? emp.gps.chId : null,
    signature: emp.signature || null,
    pointages: emp.badges ? emp.badges.map(badgeVersDto) : undefined,
    // Les messages partent un par un (`monEspaceApi.ecrit`) : renvoyer le fil
    // effaçait la réponse que la direction venait d'écrire.
    messages: undefined,
  };
}

/** API → interface : enfant de l'employé. */
function dtoVersEnfant(dto) {
  return {
    id: dto.id,
    nom: dto.nom ?? '',
    prenom: dto.prenom ?? '',
    naissance: dto.dateNaissance ?? '',
    pays: dto.pays ?? '',
  };
}

/** Interface → API : enfant de l'employé. */
function enfantVersDto(enfant) {
  return {
    id: typeof enfant.id === 'number' ? enfant.id : null,
    nom: enfant.nom || null,
    prenom: enfant.prenom || null,
    dateNaissance: dateVersIso(enfant.naissance),
    pays: enfant.pays || null,
  };
}

/** API → interface : pièce du dossier de l'employé. */
function dtoVersPiece(dto) {
  return {
    id: dto.id,
    type: dto.type ?? '',
    nom: dto.nom ?? '',
    exp: dto.expireLe ?? '',
    statut: dto.statut ?? 'reçu',
    // Le scan se joint et se retire par son propre appel : seule son
    // existence voyage avec la fiche, et ne repart pas a l'enregistrement.
    fichier: dto.fichierJoint === true,
    nomFichier: dto.nomFichier ?? '',
  };
}

/** Interface → API : pièce du dossier de l'employé. */
function pieceVersDto(piece) {
  return {
    id: typeof piece.id === 'number' ? piece.id : null,
    type: piece.type || null,
    nom: piece.nom || null,
    expireLe: dateVersIso(piece.exp),
    statut: piece.statut || null,
  };
}

/** API → interface : pointage porté par la fiche de l'employé. */
function dtoVersBadge(dto) {
  return {
    id: dto.id,
    chId: dto.chantierId ?? undefined,
    date: dto.date ?? '',
    entree: dto.entree ?? '',
    sortie: dto.sortie ?? '',
    pause: dto.pause ?? 0,
    type: dto.type ?? 'normal',
    tache: dto.tache ?? '',
    // Vide sur le chantier, « ATELIER » à l'atelier ; sans chantier encore
    // « BUREAU », « DEPOT » ou « TELETRAVAIL ».
    lieu: dto.lieu ?? '',
    // Le contrôle de présence au bureau ou au dépôt, sous les clés des
    // pointages de chantier.
    onSiteEntree: dto.surSiteEntree ?? null,
    latE: dto.latitudeEntree == null ? null : Number(dto.latitudeEntree),
    lngE: dto.longitudeEntree == null ? null : Number(dto.longitudeEntree),
    onSiteSortie: dto.surSiteSortie ?? null,
  };
}

/** Interface → API : pointage porté par la fiche de l'employé. */
function badgeVersDto(b) {
  return {
    id: typeof b.id === 'number' ? b.id : null,
    chantierId: typeof b.chId === 'number' ? b.chId : null,
    date: dateVersIso(b.date),
    // Le vide se transmet en `null`, comme le serveur le renvoie : envoyer une
    // chaine vide la ou il repond `null` ferait diverger la comparaison a
    // chaque rendu, et l'ecran reecrirait la fiche sans fin.
    entree: b.entree || null,
    sortie: b.sortie || null,
    pause: nombre(b.pause) ?? 0,
    type: b.type || 'normal',
    tache: b.tache || null,
    lieu: b.lieu || null,
    surSiteEntree: typeof b.onSiteEntree === 'boolean' ? b.onSiteEntree : null,
    // Arrondies comme le serveur les garde : sinon la copie renvoyée
    // différerait de la sienne à chaque rendu.
    latitudeEntree: coordonnee(nombre(b.latE)),
    longitudeEntree: coordonnee(nombre(b.lngE)),
    surSiteSortie: typeof b.onSiteSortie === 'boolean' ? b.onSiteSortie : null,
  };
}

/** Interface → API : article de stock. */
export function articleVersDto(article) {
  return {
    id: typeof article.id === 'number' ? article.id : null,
    reference: article.ref ?? article.reference ?? '',
    nom: article.nom ?? '',
    categorie: article.cat ?? null,
    uniteBase: article.u ?? 'pce',
    prixAchat: nombre(article.pa) ?? 0,
    prixVente: nombre(article.pv) ?? 0,
    // Nul veut dire « taux de la societe » : `nombre` rend nul pour une chaine
    // vide comme pour un champ absent, ce qui est exactement la regle voulue.
    // Zero, lui, est un taux : celui d'un article exonere.
    tauxTva: nombre(article.tva),
    quantiteStock: nombre(article.stock) ?? 0,
    seuilMinimum: nombre(article.min) ?? 0,
    fournisseur: article.fournisseur ?? null,
    description: article.description ?? null,
    codeBarre: article.codeBarre || null,
    metier: article.metier || null,
    // Une entree de stock est un fait date : le serveur ne retient que celles
    // qui n'ont pas encore d'identifiant, et ne recrit jamais les anciennes.
    achats: (article.achats ?? []).map((a) => ({
      id: typeof a.id === 'number' ? a.id : null,
      date: dateVersIso(a.date),
      quantite: nombre(a.qte),
      prixUnitaire: nombre(a.pa),
      total: nombre(a.total),
      fournisseur: a.fournisseur || null,
    })),
    // Une sortie l'est tout autant : elle dit pourquoi la quantite a baisse.
    // Sans elle, il ne restait au rechargement qu'un ecart d'inventaire sans
    // explication.
    sorties: (article.sorties ?? []).map((s) => ({
      id: typeof s.id === 'number' ? s.id : null,
      date: dateVersIso(s.date),
      heure: s.heure || null,
      quantite: nombre(s.qte),
      motif: s.motif || null,
      note: s.note || null,
      employeId: typeof s.empId === 'number' ? s.empId : null,
      employeNom: s.empNom || null,
      chantierId: typeof s.chantierId === 'number' ? s.chantierId : null,
      chantierNom: s.chantierNom || null,
    })),
    conditionnements: (article.conds ?? []).map((c) => ({
      id: typeof c.id === 'number' ? c.id : null,
      nom: c.nom ?? '',
      quantite: nombre(c.qte) ?? 0,
      parUnite: c.par ?? null,
    })),
  };
}

/** API → interface : article de stock. */
export function dtoVersArticle(dto) {
  return {
    id: dto.id,
    ref: dto.reference,
    nom: dto.nom,
    cat: dto.categorie ?? '',
    u: dto.uniteBase,
    pa: dto.prixAchat ?? 0,
    pv: dto.prixVente ?? 0,
    tva: dto.tauxTva ?? null,
    stock: dto.quantiteStock ?? 0,
    min: dto.seuilMinimum ?? 0,
    fournisseur: dto.fournisseur ?? '',
    description: dto.description ?? '',
    codeBarre: dto.codeBarre ?? '',
    metier: dto.metier ?? '',
    sousSeuil: dto.sousSeuil === true,
    marge: dto.marge ?? 0,
    conds: (dto.conditionnements ?? []).map((c) => ({
      id: c.id,
      nom: c.nom,
      qte: c.quantite,
      par: c.parUnite,
      equivalentBase: c.equivalentBase,
    })),
    // Les pieces sont en lecture seule ici : elles s'ajoutent et se retirent
    // une par une, par leurs propres appels, et ne repartent donc pas dans
    // `articleVersDto`.
    // L'historique justifie le prix d'achat moyen de la fiche : sans lui, le
    // prix reste et sa justification disparait.
    achats: (dto.achats ?? []).map((a) => ({
      id: a.id,
      date: isoVersDate(a.date),
      qte: a.quantite ?? 0,
      // Un achat noté sans prix (bon d'achat de plusieurs articles) le reste.
      pa: a.prixUnitaire ?? null,
      total: a.total ?? 0,
      fournisseur: a.fournisseur ?? '',
    })),
    // Le journal des sorties, la plus recente en tete : c'est lui qui explique
    // l'ecart entre l'inventaire et ce qui reste en rayon.
    sorties: (dto.sorties ?? []).map((s) => ({
      id: s.id,
      date: isoVersDate(s.date),
      heure: s.heure ?? '',
      qte: s.quantite ?? 0,
      motif: s.motif ?? '',
      note: s.note ?? '',
      empId: s.employeId ?? null,
      empNom: s.employeNom ?? '',
      chantierId: s.chantierId ?? null,
      chantierNom: s.chantierNom ?? '',
    })),
    medias: (dto.medias ?? []).map((m) => ({
      id: m.id,
      rang: m.rang,
      typePiece: m.typePiece,
      typeMedia: m.typeMedia,
      nomFichier: m.nomFichier,
      fichier: m.fichier,
    })),
  };
}

// ── Chantier : agrégat complet ─────────────────────────────────────────────
//
// Le chantier porte tout son suivi. Les deux conversions doivent donc rester
// symétriques : ce qui est lu ici est réécrit tel quel au prochain
// enregistrement, faute de quoi la sauvegarde effacerait les collections.

/** API → interface : pointage. */
function dtoVersPointage(dto) {
  return {
    id: dto.id,
    empId: dto.employeeId,
    empNom: dto.employeNom ?? '',
    date: dto.datePointage ?? '',
    entree: dto.heureEntree ?? '',
    sortie: dto.heureSortie ?? '',
    pause: dto.pauseMinutes ?? 0,
    type: dto.typePointage ?? 'normal',
    tache: dto.tache ?? '',
    heures: dto.heures ?? 0,
    // Controle de presence : « sur site / hors site » a l'arrivee et au
    // depart, avec la position relevee. Nul tant que rien n'a ete verifie :
    // la fiche chantier et la page GPS ne montrent le badge que s'il existe.
    onSiteEntree: dto.surSiteEntree ?? null,
    latE: dto.latitudeEntree == null ? null : Number(dto.latitudeEntree),
    lngE: dto.longitudeEntree == null ? null : Number(dto.longitudeEntree),
    onSiteSortie: dto.surSiteSortie ?? null,
    lieu: dto.lieu ?? '',
  };
}

/** Interface → API : pointage. */
function pointageVersDto(p) {
  return {
    id: typeof p.id === 'number' ? p.id : null,
    employeeId: p.empId ?? null,
    datePointage: dateVersIso(p.date),
    heureEntree: p.entree || null,
    heureSortie: p.sortie || null,
    pauseMinutes: nombre(p.pause) ?? 0,
    typePointage: p.type ?? 'normal',
    tache: p.tache ?? null,
    // Le controle de presence pose par l'espace ouvrier : un booleen, ou nul
    // quand la position n'a pas pu etre relevee.
    surSiteEntree: typeof p.onSiteEntree === 'boolean' ? p.onSiteEntree : null,
    latitudeEntree: nombre(p.latE),
    longitudeEntree: nombre(p.lngE),
    surSiteSortie: typeof p.onSiteSortie === 'boolean' ? p.onSiteSortie : null,
    lieu: p.lieu || null,
  };
}

/** API → interface : rapport journalier. */
function dtoVersRapport(dto) {
  return {
    id: dto.id,
    empId: dto.employeeId,
    empNom: dto.employeNom ?? '',
    date: isoVersDate(dto.dateRapport),
    contenu: dto.contenu ?? '',
    meteo: dto.meteo ?? '',
    materiel: dto.materiel ?? '',
    hd: dto.heureDebut ?? '',
    hf: dto.heureFin ?? '',
    heures: dto.heures ?? 0,
  };
}

/** Interface → API : rapport journalier. */
function rapportVersDto(r) {
  return {
    id: typeof r.id === 'number' ? r.id : null,
    employeeId: r.empId ?? null,
    dateRapport: dateVersIso(r.date),
    contenu: r.contenu ?? null,
    meteo: r.meteo ?? null,
    materiel: r.materiel ?? null,
    heureDebut: r.hd || null,
    heureFin: r.hf || null,
    heures: nombre(r.heures) ?? 0,
  };
}

/** API → interface : photo de chantier. */
function dtoVersPhoto(dto) {
  return {
    id: dto.id,
    cat: dto.categorie ?? 'pendant',
    label: dto.libelle ?? '',
    emoji: dto.emoji ?? '',
    date: isoVersDate(dto.datePrise),
    h: dto.heurePrise ?? '',
    auteur: dto.auteur ?? '',
    // L'image n'accompagne pas le chantier : l'écran la demande à son adresse
    // au moment de l'afficher, et le navigateur la met en cache.
    src: dto.image ? adresseApi(dto.image) : undefined,
    // L'artisan choisit photo par photo celles que le client verra. Le choix
    // ne quittait pas l'écran, et le portail du client restait vide.
    partageClient: dto.partageClient === true,
    // Le poste regroupe les photos (« repris de l'offre ») et la note legende
    // la carte ; sans eux, tout retombait sous « General » et disait « Photo ».
    poste: dto.poste ?? '',
    note: dto.note ?? '',
  };
}

/** Interface → API : photo de chantier. */
function photoVersDto(p) {
  // Seule la description circule avec le chantier : l'image vit hors de la base
  // et s'envoie par son propre point d'entrée, en pièce jointe.
  return {
    id: typeof p.id === 'number' ? p.id : null,
    categorie: p.cat ?? 'pendant',
    libelle: p.label ?? null,
    emoji: p.emoji ?? null,
    datePrise: dateVersIso(p.date),
    heurePrise: p.h ?? null,
    auteur: p.auteur ?? null,
    partageClient: p.partageClient === true,
    poste: p.poste || null,
    note: p.note || null,
  };
}

/** API → interface : plan de chantier. */
function dtoVersPlan(dto) {
  return {
    id: dto.id,
    nom: dto.nom,
    emoji: dto.emoji ?? '',
    type: dto.typeFichier ?? '',
    version: dto.version ?? 1,
    date: isoVersDate(dto.dateDepot),
    taille: dto.taille ?? '',
    envoye: dto.envoye === true,
    // Comme la photo, le plan vit hors de la base : la ligne n'en porte que
    // l'adresse. Sans elle, l'écran affichait un nom de plan sans plan.
    src: dto.fichier ? adresseApi(dto.fichier) : undefined,
  };
}

/** Interface → API : plan de chantier. */
function planVersDto(p) {
  return {
    id: typeof p.id === 'number' ? p.id : null,
    nom: p.nom ?? '',
    emoji: p.emoji ?? null,
    typeFichier: p.type ?? null,
    version: nombre(p.version) ?? 1,
    dateDepot: dateVersIso(p.date),
    taille: p.taille ?? null,
    envoye: p.envoye === true,
  };
}

/** API → interface : sous-traitant. */
function dtoVersSousTraitant(dto) {
  return {
    id: dto.id,
    entreprise: dto.entreprise,
    contact: dto.contact ?? '',
    tel: dto.telephone ?? '',
    metier: dto.metier ?? '',
    prixConvenu: dto.prixConvenu ?? 0,
    paye: dto.montantPaye ?? 0,
    soldeDu: dto.soldeDu ?? 0,
    // Le scan de la facture ou du contrat est une piece jointe du chantier :
    // la ligne en porte la reference, l'ecran l'affiche a son adresse.
    pieceId: dto.pieceJointeId ?? null,
    photo: dto.piece ? adresseApi(dto.piece) : '',
    // La TVA imprimée sur sa facture, lue au scan ; null, elle est estimée.
    montantTva: dto.montantTva ?? null,
    // Le jour où il a été payé, et comment : saisi, ou trouvé dans le relevé.
    payeLe: isoVersDate(dto.payeLe),
    payePar: dto.payePar ? String(dto.payePar).toLowerCase() : '',
  };
}

/** Interface → API : sous-traitant. */
function sousTraitantVersDto(t) {
  return {
    id: typeof t.id === 'number' ? t.id : null,
    entreprise: t.entreprise ?? '',
    contact: t.contact ?? null,
    telephone: t.tel ?? null,
    metier: t.metier ?? null,
    prixConvenu: nombre(t.prixConvenu) ?? 0,
    montantPaye: nombre(t.paye) ?? 0,
    pieceJointeId: typeof t.pieceId === 'number' ? t.pieceId : null,
    montantTva: nombre(t.montantTva),
    payeLe: dateVersIso(t.payeLe),
    payePar: t.payeLe ? moyenVersDto(t.payePar) : null,
  };
}

/** API → interface : facture fournisseur. */
function dtoVersFactureFournisseur(dto) {
  return {
    id: dto.id,
    fournisseur: dto.fournisseur,
    montant: dto.montant ?? 0,
    date: isoVersDate(dto.dateFacture),
    ref: dto.reference ?? '',
    cat: dto.categorie ?? '',
    // L'IBAN fait vivre le bouton « Payer (QR) » ; le scan est une piece
    // jointe du chantier, affichee a son adresse. Perdus au rechargement.
    iban: dto.iban ?? '',
    pieceId: dto.pieceJointeId ?? null,
    photo: dto.piece ? adresseApi(dto.piece) : '',
    // TVA de la facture, telle qu'elle y figure : elle entre dans la TVA à
    // récupérer. Vide, la TVA est estimée au taux normal.
    tauxTva: dto.tauxTva ?? null,
    montantTva: dto.montantTva ?? null,
    // Le matériel acheté, lu sur la facture.
    articles: dto.articles ?? '',
    // Le jour où elle a été payée, et comment : saisi, ou trouvé dans le relevé.
    payeLe: isoVersDate(dto.payeLe),
    payePar: dto.payePar ? String(dto.payePar).toLowerCase() : '',
  };
}

/** Interface → API : facture fournisseur. */
function factureFournisseurVersDto(f) {
  return {
    id: typeof f.id === 'number' ? f.id : null,
    fournisseur: f.fournisseur ?? '',
    montant: nombre(f.montant) ?? 0,
    dateFacture: dateVersIso(f.date),
    reference: f.ref ?? null,
    categorie: f.cat ?? null,
    iban: f.iban ? String(f.iban).replace(/\s+/g, '').toUpperCase() : null,
    pieceJointeId: typeof f.pieceId === 'number' ? f.pieceId : null,
    tauxTva: nombre(f.tauxTva),
    montantTva: nombre(f.montantTva),
    articles:
      String(f.articles || '')
        .trim()
        .slice(0, 300) || null,
    payeLe: dateVersIso(f.payeLe),
    payePar: f.payeLe ? moyenVersDto(f.payePar) : null,
  };
}

/** API → interface : message de chantier. */
function dtoVersMessageChantier(dto) {
  return {
    id: dto.id,
    de: dto.auteur ?? 'gerant',
    contenu: dto.contenu ?? '',
    date: dto.dateMessage ?? '',
    lu: dto.lu === true,
  };
}

/** Interface → API : message de chantier. */
function messageChantierVersDto(m) {
  return {
    id: typeof m.id === 'number' ? m.id : null,
    auteur: m.de ?? 'gerant',
    contenu: m.contenu ?? '',
    dateMessage: m.date && String(m.date).includes('T') ? m.date : null,
    lu: m.lu === true,
  };
}

/** API → interface : avenant. */
function dtoVersAvenant(dto) {
  return {
    id: dto.id,
    num: dto.numero ?? '',
    date: isoVersDate(dto.dateAvenant),
    montant: dto.montant ?? 0,
    // L'écran dit « motif », et c'est lui qui dit pourquoi le client doit
    // 2'400 francs de plus. Sans ce mot-là, la liste des avenants n'était
    // qu'une suite de montants sans raison.
    motif: dto.description ?? '',
    desc: dto.description ?? '',
    sig: dto.signature ?? '',
    lignes: (dto.lignes ?? []).map((l) => ({
      id: l.id,
      d: l.designation,
      u: l.unite ?? '',
      q: l.quantite ?? 0,
      p: l.prixUnitaire ?? 0,
    })),
  };
}

/** Interface → API : avenant. */
function avenantVersDto(a) {
  return {
    id: typeof a.id === 'number' ? a.id : null,
    numero: a.num ?? null,
    dateAvenant: dateVersIso(a.date),
    montant: nombre(a.montant) ?? 0,
    description: a.motif ?? a.desc ?? null,
    signature: a.sig ?? null,
    lignes: (a.lignes ?? []).map((l, i) => ({
      id: typeof l.id === 'number' ? l.id : null,
      position: i,
      designation: l.d ?? '',
      unite: l.u ?? null,
      quantite: nombre(l.q) ?? 0,
      prixUnitaire: nombre(l.p) ?? 0,
    })),
  };
}

/** API → interface : matériau consommé. */
function dtoVersMateriau(dto) {
  return {
    id: dto.id,
    d: dto.designation,
    nom: dto.designation,
    ref: dto.referenceArticle ?? '',
    u: dto.unite ?? '',
    qte: dto.quantite ?? 0,
    pa: dto.prixAchat ?? 0,
    date: isoVersDate(dto.dateSortie),
    heure: (dto.heureSortie ?? '').slice(0, 5),
    empNom: dto.sortiPar ?? '',
    source: dto.provenance === 'FOURNISSEUR' ? 'fournisseur' : 'depot',
    cout: dto.cout ?? 0,
    // L'achat fournisseur qu'une ligne de retour au dépôt reprend.
    retourDe: dto.retourDe ?? null,
  };
}

/**
 * Interface → API : matériau consommé.
 *
 * <p>La désignation est acceptée sous trois noms : l'espace ouvrier compose le
 * matériau à partir de l'article de stock, qui nomme son libellé `nom`, là où
 * l'écran chantier emploie `d`. Ne lire qu'une seule de ces clés reviendrait à
 * enregistrer un matériau sans désignation, que le serveur refuse.
 */
function materiauVersDto(m) {
  return {
    id: typeof m.id === 'number' ? m.id : null,
    designation: m.d ?? m.nom ?? m.designation ?? '',
    referenceArticle: m.ref ?? null,
    unite: m.u ?? null,
    quantite: nombre(m.qte) ?? 0,
    prixAchat: nombre(m.pa) ?? 0,
    dateSortie: dateVersIso(m.date),
    heureSortie: m.heure ? `${m.heure}:00` : null,
    sortiPar: m.empNom ?? null,
    provenance: m.source === 'fournisseur' ? 'FOURNISSEUR' : 'DEPOT',
  };
}

/**
 * API → interface : chantier avec tout son suivi.
 *
 * @param {object} dto chantier renvoyé par l'API
 * @returns {object} le chantier au format attendu par l'interface
 */
export function dtoVersChantierDetail(dto) {
  return {
    ...dtoVersChantier(dto),
    pv: dto.pv
      ? { date: isoVersDate(dto.pv.date), retenue: dto.pv.retenue, reserves: dto.pv.reserves ?? '' }
      : null,
    sousTraitants: (dto.sousTraitants ?? []).map(dtoVersSousTraitant),
    factures: (dto.facturesFournisseurs ?? []).map(dtoVersFactureFournisseur),
    pointages: (dto.pointages ?? []).map(dtoVersPointage),
    rapports: (dto.rapports ?? []).map(dtoVersRapport),
    photos: (dto.photos ?? []).map(dtoVersPhoto),
    plans: (dto.plans ?? []).map(dtoVersPlan),
    planif: (dto.affectations ?? []).map((a) => ({
      id: a.id,
      date: a.dateAffectation,
      empId: a.employeeId,
    })),
    messages: (dto.messages ?? []).map(dtoVersMessageChantier),
    avenants: (dto.avenants ?? []).map(dtoVersAvenant),
    materiaux: (dto.materiaux ?? []).map(dtoVersMateriau),
    // Ce que l'écran a lu du serveur : une ligne ajoutée depuis par un
    // collègue n'est pas prise pour une ligne supprimée ici.
    _connus: {
      pointages: idsServeur(dto.pointages),
      rapports: idsServeur(dto.rapports),
      messages: idsServeur(dto.messages),
      materiaux: idsServeur(dto.materiaux),
    },
  };
}

/** Identifiants numériques des lignes reçues du serveur. */
function idsServeur(lignes) {
  return (lignes ?? []).map((l) => l?.id).filter((id) => typeof id === 'number');
}

/**
 * Interface → API : chantier avec tout son suivi.
 *
 * @param {object} chantier chantier au format de l'interface
 * @returns {object} l'agrégat attendu par l'API
 */
export function chantierDetailVersDto(chantier) {
  return {
    ...chantierVersDto(chantier),
    pv: chantier.pv
      ? {
          date: dateVersIso(chantier.pv.date),
          retenue: nombre(chantier.pv.retenue),
          reserves: chantier.pv.reserves ?? null,
        }
      : null,
    sousTraitants: (chantier.sousTraitants ?? []).map(sousTraitantVersDto),
    facturesFournisseurs: (chantier.factures ?? []).map(factureFournisseurVersDto),
    pointages: (chantier.pointages ?? []).map(pointageVersDto),
    rapports: (chantier.rapports ?? []).map(rapportVersDto),
    photos: (chantier.photos ?? []).map(photoVersDto),
    plans: (chantier.plans ?? []).map(planVersDto),
    affectations: (chantier.planif ?? [])
      .filter((a) => a.empId != null)
      .map((a) => ({
        id: typeof a.id === 'number' ? a.id : null,
        employeeId: a.empId,
        dateAffectation: dateVersIso(a.date),
      })),
    messages: (chantier.messages ?? []).map(messageChantierVersDto),
    avenants: (chantier.avenants ?? []).map(avenantVersDto),
    materiaux: (chantier.materiaux ?? []).map(materiauVersDto),
    connus: chantier._connus ?? null,
  };
}

// ── Société ────────────────────────────────────────────────────────────────

/** API → interface : paramètres de la société. */
/**
 * Réglages d'entreprise que le schéma ne porte pas en colonne.
 *
 * <p>Ils vivaient uniquement dans l'état de l'écran : l'IBAN saisi dans les
 * réglages, le tampon du patron, les taux de charges sociales, les codes
 * d'accès — tout cela disparaissait au rechargement, et l'écran des réglages
 * ne faisait que le mimer. Une entreprise mixte perdait jusqu'à son identité :
 * ses coordonnées par secteur vivent dans {@code profils}, et sans elles la
 * QR-facture n'avait pas d'IBAN à présenter.
 *
 * <p>Ils voyagent dans le sac de paramètres de la société, sous un préfixe qui
 * les sépare des réglages d'application ({@code cmdW3Key} et consorts). Chacun
 * est écrit en JSON : c'est ce qui rend fidèlement un tableau de taux comme une
 * case à cocher, sans avoir à dire de quel type est chaque réglage.
 */
const CHAMPS_SOCIETE_EN_PARAMETRES = [
  'acces',
  // Valeurs par défaut des nouveaux documents (Réglages → Documents).
  'acompteDefaut',
  'remiseDefaut',
  'delaiPaiementDefaut',
  'tvaDefaut',
  'validiteOffreDefaut',
  // Les trois taux legaux et leur date d'entree en vigueur (Reglages → Documents → Taux de TVA).
  'tvaHistorique',
  'tvaMethode',
  'afficher',
  'banniere',
  'banque',
  'capital',
  // Listes de « Mon activité » (Réglages → Préférences) et de la carte Unités :
  // unités des devis, catégories de dépenses, corps de métier / rayons. Elles
  // ne vivaient qu'à l'écran et revenaient aux valeurs du métier au
  // rechargement.
  // Canton choisi dans les réglages (taux des charges sociales) ; sans lui, celui du code postal.
  'canton',
  'cats',
  'catsMag',
  'chargesPerso',
  'codeSociete',
  'devisStyle',
  'docFont',
  'docSize',
  'filigrane',
  'fournisseurs',
  'garantieAns',
  'logoPos',
  'membres',
  // Corps de métier / rayons enlevés de la liste de l'offre (touche ✕) :
  // leurs articles restent au catalogue.
  'metiersCaches',
  'metiersCachesMag',
  'mode13',
  'motChantier',
  'motOffre',
  'msgEnvoi',
  'paiement',
  // QR code du pays (Réglages → Banque) : identifiant de paiement (UPI, Pix…),
  // code de la banque (VietQR), ou image du QR délivré par la banque.
  'qrBanque',
  'qrId',
  'qrImage',
  // Bulletin de versement remis par la banque (« Bulletin de ma banque »).
  'bulletinImage',
  // Compte et titulaire lus dans son QR code : la facture refait alors le
  // bulletin officiel, rempli du client, du montant et de la référence.
  'bulletinLu',
  // Qui voit et envoie les attestations de l'entreprise (Réglages → Accès).
  'permAttestations',
  'permSalaire',
  'permStock',
  'profils',
  'rappelAuto',
  'rayonsBat',
  'rayonsMag',
  'recurOn',
  'releveBancaire',
  'reseauxShow',
  'showIban',
  'showMentions',
  'showQr',
  'sigPatron',
  'suggestions',
  'tampon',
  'tamponSur',
  // Taux de charges sociales : `taux`, sans année, est l'ancien réglage, lu
  // comme celui du barème ; `tauxParAnnee` porte un jeu par année.
  'taux',
  // Taux propres à la comptabilité (LPP, CCT, RETABAT, impôts) ; AVS, AC,
  // LAA, AF, frais d'administration et IJM sont lus dans le barème de l'année,
  // comme pour la paie.
  'tauxCompta',
  'tauxParAnnee',
  // Hors de Suisse, les cotisations du pays corrigées ligne par ligne, par
  // année et par nom de ligne ; la fiche du serveur les lit aussi.
  'lignesPays',
  'tplDevis',
  'tplFacture',
  'unites',
  'unitesMag',
];

const PREFIXE_SOCIETE = 'soc.';

/** Réglages qu'il faut nettoyer avant de les écrire ou après les avoir lus. */
const NETTOYAGE_REGLAGES = { membres: membresSansCode };

const nettoie = (champ, valeur) =>
  NETTOYAGE_REGLAGES[champ] ? NETTOYAGE_REGLAGES[champ](valeur) : valeur;

/** Range dans le sac de paramètres les réglages qui n'ont pas de colonne. */
function parametresAvecReglages(soc) {
  const sortie = { ...(soc.parametres ?? {}) };
  for (const champ of CHAMPS_SOCIETE_EN_PARAMETRES) {
    const cle = PREFIXE_SOCIETE + champ;
    const valeur = nettoie(champ, soc[champ]);
    if (valeur === undefined || valeur === null || valeur === '') {
      delete sortie[cle];
    } else {
      sortie[cle] = JSON.stringify(valeur);
    }
  }
  return sortie;
}

/** Ressort du sac de paramètres les réglages qui n'ont pas de colonne. */
function reglagesDepuisParametres(parametres) {
  const sortie = {};
  for (const champ of CHAMPS_SOCIETE_EN_PARAMETRES) {
    const brut = parametres?.[PREFIXE_SOCIETE + champ];
    if (brut === undefined || brut === null || brut === '') continue;
    try {
      sortie[champ] = nettoie(champ, JSON.parse(brut));
    } catch (e) {
      // Un réglage écrit par une version plus ancienne, ou tronqué : le rendre
      // tel quel vaut mieux que de faire échouer le chargement de l'entreprise.
      sortie[champ] = brut;
    }
  }
  return sortie;
}

/** Les paramètres d'application seuls, sans les réglages d'entreprise. */
function parametresSansReglages(parametres) {
  const sortie = {};
  for (const [cle, valeur] of Object.entries(parametres ?? {})) {
    if (!cle.startsWith(PREFIXE_SOCIETE)) sortie[cle] = valeur;
  }
  return sortie;
}

export function dtoVersSociete(dto) {
  if (!dto) return null;
  return {
    id: dto.id,
    nom: dto.nom ?? '',
    forme: dto.formeJuridique ?? '',
    adr: dto.adresse ?? '',
    npa: dto.npa ?? '',
    tel: dto.telephone ?? '',
    email: dto.email ?? '',
    rc: dto.registreCommerce ?? '',
    tva: dto.numeroTva ?? '',
    iban: dto.iban ?? '',
    bic: dto.bic ?? '',
    logo: dto.logo ?? '',
    // Le batiment s'ecrit « batiment ». La carte « Secteur d'activite » a
    // longtemps ecrit « chantier » — le mot de l'univers d'une societe mixte —
    // et les societes enregistrees ainsi passaient pour n'etre d'aucun secteur
    // aux yeux de la comptabilite. Ramener le mot ici corrige chaque lecture
    // d'un coup, sans reprendre les fiches une a une.
    secteur: dto.secteur === 'chantier' ? 'batiment' : (dto.secteur ?? ''),
    metier: dto.metier ?? '',
    pays: dto.pays ?? 'CH',
    devise: dto.devise ?? 'CHF',
    lang: dto.langue ?? 'fr',
    tvaPct: dto.tauxTva,
    acompteDelai: dto.acompteDelaiJours ?? 10,
    docStyle: dto.docStyle ?? '',
    docCouleur: dto.docCouleur ?? '',
    mentionsDevis: dto.mentionsDevis ?? '',
    mentionsFacture: dto.mentionsFacture ?? '',
    reseaux: dto.reseaux ?? {},
    abonne: dto.abonne === true,
    essaiDebut: dto.essaiDebut ?? null,
    // Formule, état et prix, calculés par le serveur : en lecture seule, le
    // choix passe par sa propre route et n'est pas renvoyé avec la fiche.
    abonnement: dto.abonnement ?? null,
    // Le code que les ouvriers saisissent sur chantier. En lecture seule : il
    // est tiré au sort par le serveur, non choisi, et n'est donc pas renvoyé.
    codeOuvrier: dto.codeOuvrier ?? '',
    parametres: parametresSansReglages(dto.parametres),
    ...reglagesDepuisParametres(dto.parametres),
  };
}

/** Interface → API : paramètres de la société. */
export function societeVersDto(soc) {
  return {
    id: typeof soc.id === 'number' ? soc.id : null,
    nom: soc.nom ?? '',
    formeJuridique: soc.forme ?? null,
    adresse: soc.adr ?? null,
    npa: soc.npa ?? null,
    telephone: soc.tel ?? null,
    email: soc.email || null,
    registreCommerce: soc.rc ?? null,
    numeroTva: soc.tva ?? null,
    iban: soc.iban ?? null,
    bic: soc.bic ?? null,
    logo: soc.logo ?? null,
    secteur: soc.secteur ?? null,
    metier: soc.metier ?? null,
    pays: soc.pays ?? 'CH',
    devise: soc.devise ?? 'CHF',
    langue: soc.lang ?? 'fr',
    tauxTva: nombre(soc.tvaPct),
    acompteDelaiJours: nombre(soc.acompteDelai) ?? 10,
    docStyle: soc.docStyle ?? null,
    docCouleur: soc.docCouleur ?? null,
    mentionsDevis: soc.mentionsDevis ?? null,
    mentionsFacture: soc.mentionsFacture ?? null,
    reseaux: soc.reseaux ?? {},
    abonne: soc.abonne === true,
    essaiDebut: soc.essaiDebut ?? null,
    parametres: parametresAvecReglages(soc),
  };
}

// ── Congés ─────────────────────────────────────────────────────────────────

const STATUTS_CONGE = {
  'En attente': 'EN_ATTENTE',
  Approuvé: 'APPROUVE',
  Refusé: 'REFUSE',
  'Date proposée': 'DATE_PROPOSEE',
};

/** Interface → API : statut de congé. */
export function statutCongeVersApi(statut) {
  return STATUTS_CONGE[statut] ?? String(statut ?? '').toUpperCase();
}

/** API → interface : statut de congé. */
export function statutCongeVersInterface(statut) {
  const entree = Object.entries(STATUTS_CONGE).find(([, api]) => api === statut);
  return entree ? entree[0] : statut;
}

/** API → interface : demande de congé. */
export function dtoVersConge(dto) {
  return {
    id: dto.id,
    empId: dto.employeeId,
    empNom: dto.employeNom ?? '',
    debut: dto.dateDebut,
    fin: dto.dateFin,
    type: dto.typeConge ?? 'vacances',
    jours: dto.nombreJours ?? 1,
    statut: statutCongeVersInterface(dto.statut),
    motif: dto.motif ?? '',
    // Autre date proposee par la direction : la decision et la reponse
    // passent par leurs propres appels, et ne repartent pas a l'enregistrement.
    debutPropose: dto.dateProposeeDebut ?? '',
    finPropose: dto.dateProposeeFin ?? '',
    // Le certificat medical ne voyage jamais avec la liste : seul son
    // existence s'y lit, le fichier se demande a part, a qui y a droit.
    certificat: dto.certificatJoint === true,
    certificatNom: dto.certificatNom ?? '',
  };
}

/** Interface → API : demande de congé. */
export function congeVersDto(conge) {
  return {
    id: typeof conge.id === 'number' ? conge.id : null,
    employeeId: conge.empId ?? null,
    dateDebut: dateVersIso(conge.debut),
    dateFin: dateVersIso(conge.fin),
    typeConge: conge.type ?? 'vacances',
    nombreJours: nombre(conge.jours) ?? 1,
    statut: conge.statut ? statutCongeVersApi(conge.statut) : null,
    motif: conge.motif ?? null,
  };
}

// ── Dépenses ───────────────────────────────────────────────────────────────

/**
 * Type de média d'un justificatif, lu dans son adresse de données.
 *
 * L'écran distingue l'image, qu'il affiche, du PDF, qu'il annonce. Le type se
 * trouve en tête de l'adresse : le conserver dans une colonne à part ferait
 * deux vérités pour un seul fait.
 *
 * @param {string} [adresse] justificatif encodé
 * @returns {string} le type de média, ou une chaîne vide
 */
function typeDuJustificatif(adresse) {
  const entete = /^data:([\w.+-]+\/[\w.+-]+);base64,/.exec(adresse || '');
  return entete ? entete[1] : '';
}

/** API → interface : dépense. */
export function dtoVersDepense(dto) {
  return {
    id: dto.id,
    // L'écran des dépenses travaille sur `libelle`, `type` et `piece` ; la
    // comptabilité ventile sur `type`. Sans ces trois-là, une dépense
    // rechargée revenait sans intitulé, hors de tout compte — et la TVA
    // récupérable retombait à zéro.
    lib: dto.libelle,
    libelle: dto.libelle ?? '',
    date: isoVersDate(dto.dateDepense),
    dateCompta: isoVersDate(dto.dateCompta),
    montant: dto.montant ?? 0,
    tva: dto.tauxTva,
    compte: dto.compteCode ?? '',
    compteLib: dto.compteLibelle ?? '',
    cat: dto.categorie ?? '',
    type: dto.categorie ?? '',
    fournisseur: dto.fournisseur ?? '',
    chantierId: dto.siteId,
    iban: dto.iban ?? '',
    justificatif: dto.justificatif ?? undefined,
    piece: dto.justificatif ?? '',
    pieceNom: dto.justificatifNom ?? '',
    pieceMime: typeDuJustificatif(dto.justificatif),
    // TVA payée, lue sur le ticket ou saisie ; vide, elle est estimée au taux
    // de la dépense.
    montantTva: dto.montantTva ?? null,
    // La photo ou le PDF d'origine, gardé à part sans réduction.
    originalId: dto.originalId ?? null,
    // Payée en espèces, par carte ou par la banque : « especes », « carte »,
    // « banque », ou '' si pas dit.
    paiement: dto.paiement ? String(dto.paiement).toLowerCase() : '',
    // Le jour où elle a été payée : saisi, ou trouvé dans le relevé de banque.
    payeLe: isoVersDate(dto.payeLe),
    // Payée chaque mois, trimestre, semestre ou année : « mensuel »,
    // « trimestriel », « semestriel », « annuel », ou '' si payée une fois.
    frequence: dto.frequence ? String(dto.frequence).toLowerCase() : '',
    // Les mois que ce paiement couvre, quand sa charge y est répartie.
    moisCouverts: dto.moisCouverts ?? null,
    // La série d'une dépense qui revient, dont le serveur note les paiements
    // suivants ; arrêtée, il n'en note plus.
    serie: dto.serie ?? null,
    serieArretee: dto.serieArretee === true,
  };
}

/** Interface → API : dépense. */
export function depenseVersDto(d) {
  return {
    id: typeof d.id === 'number' ? d.id : null,
    libelle: d.libelle ?? d.lib ?? '',
    dateDepense: dateVersIso(d.date),
    dateCompta: dateVersIso(d.dateCompta),
    montant: nombre(d.montant) ?? 0,
    tauxTva: nombre(d.tva),
    compteCode: d.compte || null,
    categorie: d.type ?? d.cat ?? null,
    fournisseur: d.fournisseur ?? null,
    siteId: nombre(d.chantierId),
    iban: d.iban || null,
    justificatif: d.piece ?? d.justificatif ?? null,
    justificatifNom: d.pieceNom || null,
    montantTva: nombre(d.montantTva),
    originalId: d.originalId ?? null,
    paiement: moyenVersDto(d.paiement),
    // Un ticket payé en espèces ou par carte l'a été le jour même.
    payeLe:
      dateVersIso(d.payeLe) ||
      (d.paiement === 'especes' || d.paiement === 'carte' ? dateVersIso(d.date) : null),
    frequence: frequenceVersDto(d.frequence),
    // Un seul mois, c'est compter le paiement en entier : rien à garder.
    moisCouverts:
      nombre(d.moisCouverts) > 1 ? Math.min(12, Math.round(nombre(d.moisCouverts))) : null,
    serie: d.serie || null,
  };
}

/** « banque », « carte », « especes » → BANQUE, CARTE, ESPECES ; autre chose → null. */
function moyenVersDto(moyen) {
  const m = String(moyen || '').toUpperCase();
  return ['BANQUE', 'CARTE', 'ESPECES'].includes(m) ? m : null;
}

/** « mensuel », « trimestriel », « semestriel », « annuel » → MENSUEL… ; autre chose → null. */
function frequenceVersDto(frequence) {
  const f = String(frequence || '').toUpperCase();
  return ['MENSUEL', 'TRIMESTRIEL', 'SEMESTRIEL', 'ANNUEL'].includes(f) ? f : null;
}

// ── Ventes ─────────────────────────────────────────────────────────────────

/** API → interface : vente comptoir. */
export function dtoVersVente(dto) {
  return {
    id: dto.id,
    clientId: dto.clientId,
    clientNom: dto.clientNom ?? '',
    date: isoVersDate(dto.dateVente),
    total: dto.montantTotal ?? 0,
    // L'ecran de caisse ecrit `pay`, non `paiement` : la cle ne correspondait
    // pas, et le mode de reglement se perdait a chaque rechargement. Les deux
    // sont rendues, le temps que les ecrans se rejoignent.
    paiement: dto.modePaiement ?? '',
    pay: dto.modePaiement ?? '',
    ref: dto.reference ?? '',
    // Une vente ne gardait que son total : le journal d'un magasin revenait
    // comme une suite de montants sans objet.
    nom: dto.articleNom ?? '',
    u: dto.unite ?? '',
    q: dto.quantite ?? 0,
    pv: dto.prixUnitaire ?? 0,
    tx: dto.tauxTva ?? null,
    ticket: dto.ticket ?? null,
    // Un avoir rembourse une ligne du ticket d'origine : il la nomme, pour
    // qu'on ne rende jamais plus que ce qui a ete vendu.
    ticketOrigineId: dto.ticketOrigineId ?? null,
    ticketOrigine: dto.ticketOrigine ?? null,
    motif: dto.motif ?? '',
  };
}

/** Interface → API : vente comptoir. */
export function venteVersDto(v) {
  return {
    id: typeof v.id === 'number' ? v.id : null,
    clientId: nombre(v.clientId),
    dateVente: dateVersIso(v.date),
    montantTotal: nombre(v.total) ?? 0,
    modePaiement: v.pay ?? v.paiement ?? null,
    reference: v.ref ?? null,
    articleNom: v.nom ?? null,
    unite: v.u ?? null,
    // Le serveur ne conserve ni la quantite ni le prix unitaire d'une vente :
    // envoyer un zero la, ou il renvoie une absence, faisait reecrire chaque
    // vente a chaque salve.
    quantite: v.q ? nombre(v.q) : undefined,
    prixUnitaire: v.pv ? nombre(v.pv) : undefined,
    // Le taux reellement applique a la ligne, fige : une vente est une piece
    // comptable, et le rapport Z de l'an dernier doit montrer le taux d'alors,
    // meme si la societe en change depuis.
    tauxTva: nombre(v.tx),
    // L'ecran de caisse appelait « tx » le numero de ticket, que ce mappeur
    // lisait comme le taux. `nombre` d'un identifiant base 36 rend nul : les
    // deux se perdaient a l'enregistrement, et la cloture comptait une ligne
    // pour un ticket — trois clients servis la ou un seul avait achete trois
    // articles.
    ticket: v.ticket == null ? null : String(v.ticket),
    // Absent pour une vente : le serveur ne verrait qu'un nul, et une ligne
    // ordinaire n'a pas a passer par la regle de l'avoir.
    ticketOrigineId: nombre(v.ticketOrigineId),
    motif: v.motif || null,
  };
}

// ── Lignes de commande et de livraison ─────────────────────────────────────

/** API → interface : ligne de commande ou de livraison. */
function dtoVersLigneLogistique(l) {
  return {
    id: l.id,
    d: l.designation,
    u: l.unite ?? '',
    q: l.quantite ?? 0,
    p: l.prixUnitaire ?? 0,
    rem: l.remise ?? 0,
    ref: l.referenceArticle ?? undefined,
    // Le taux de l'article suit la ligne jusqu'a la facture du bon ; nul vaut
    // « taux de la societe ».
    tva: l.tauxTva ?? undefined,
    // L'etat vit sur la ligne, non dans une table indexee par position : deux
    // lignes qui changent d'ordre echangeaient leurs etats, et la facture est
    // composee des seules lignes livrees.
    etat: l.etat || 'livre',
    charge: l.charge === true,
  };
}

/** Interface → API : ligne de commande ou de livraison. */
function ligneLogistiqueVersDto(l, position) {
  return {
    id: typeof l.id === 'number' ? l.id : null,
    position,
    designation: l.d ?? '',
    unite: l.u ?? null,
    quantite: nombre(l.q) ?? 0,
    prixUnitaire: nombre(l.p) ?? 0,
    remise: nombre(l.rem) ?? 0,
    tauxTva: nombre(l.tva),
    referenceArticle: l.ref ?? null,
    etat: l.etat || 'livre',
    charge: l.charge === true,
  };
}

// ── Commandes ──────────────────────────────────────────────────────────────

const STATUTS_COMMANDE = {
  Reçue: 'RECUE',
  'À préparer': 'A_PREPARER',
  'En préparation': 'EN_PREPARATION',
  Prête: 'PRETE',
  Livrée: 'LIVREE',
  Annulée: 'ANNULEE',
};

/** Interface → API : statut de commande. */
export function statutCommandeVersApi(statut) {
  return STATUTS_COMMANDE[statut] ?? String(statut ?? '').toUpperCase();
}

/** API → interface : statut de commande. */
export function statutCommandeVersInterface(statut) {
  const entree = Object.entries(STATUTS_COMMANDE).find(([, api]) => api === statut);
  return entree ? entree[0] : statut;
}

/**
 * Vrai pour une commande arrivée de l'extérieur : importée par code, ou
 * passée en ligne. Le badge « 🔗 en ligne » ne tenait qu'à un drapeau de
 * l'écran, que le serveur ne connaît pas ; il disparaissait au rechargement.
 *
 * @param {string} [source] origine de la commande, telle que l'API la porte
 * @returns {boolean} vrai si elle n'a pas été saisie à la main
 */
export function estCommandeEnLigne(source) {
  return !!source && source !== 'interne';
}

/** API → interface : commande client. */
export function dtoVersCommande(dto) {
  const source = dto.source ?? 'interne';
  return {
    id: dto.id,
    num: dto.numero,
    clientId: dto.clientId,
    clientNom: dto.clientNom ?? '',
    date: isoVersDate(dto.dateCommande),
    statut: statutCommandeVersInterface(dto.statut),
    // « livAdr », le nom de la maquette : l'écran des commandes lit et écrit
    // ce champ. Rangée sous « adr », l'adresse de livraison n'était jamais
    // envoyée au serveur ni relue.
    livAdr: dto.adresseLivraison ?? '',
    demandePar: dto.demandeur ?? '',
    source,
    enLigne: estCommandeEnLigne(source),
    notes: dto.notes ?? '',
    lignes: (dto.lignes ?? []).map(dtoVersLigneLogistique),
    total: dto.montantTotal ?? 0,
    blId: dto.blId ?? undefined,
  };
}

/** Interface → API : commande client. */
/**
 * Ligne de commande vers l'API : sans état ni chargement, que le serveur ne
 * conserve que sur un bon de livraison. Les envoyer là où il les renvoie
 * absents faisait réécrire chaque commande à chaque salve.
 */
function ligneDeCommandeVersDto(l) {
  const { etat: _etat, charge: _charge, ...reste } = ligneLogistiqueVersDto(l);
  return reste;
}

export function commandeVersDto(c) {
  return {
    id: typeof c.id === 'number' ? c.id : null,
    numero: c.num ?? (typeof c.id === 'string' ? c.id : null),
    clientId: nombre(c.clientId),
    dateCommande: dateVersIso(c.date),
    statut: c.statut ? statutCommandeVersApi(c.statut) : null,
    adresseLivraison: c.livAdr ?? null,
    demandeur: c.demandePar ?? null,
    source: c.source ?? 'interne',
    notes: c.notes ?? null,
    lignes: (c.lignes ?? []).map(ligneDeCommandeVersDto),
    // Sans lui, la commande ne savait plus quel bon elle avait produit, et
    // l'ecran proposait d'en creer un second. C'est la reference du bon,
    // « BL-2026-001 » : l'ecran le nomme avant de l'envoyer, et la commande
    // part parfois avant lui, donc un numero d'enregistrement n'existerait
    // pas encore a cet instant-la.
    blId: c.blId ? String(c.blId) : null,
  };
}

// ── Bons de livraison ──────────────────────────────────────────────────────

// L'ecran des livraisons emploie ce vocabulaire-la, et nul autre. Les trois
// entrees qui s'y trouvaient ne le couvraient pas : « Signe » repartait en
// « SIGNÉ », que le serveur ne connaissait pas, et signer un bon se soldait
// par une erreur au lieu d'un enregistrement.
const STATUTS_LIVRAISON = {
  'À livrer': 'A_LIVRER',
  'En route': 'EN_ROUTE',
  Livrée: 'LIVRE',
  Partielle: 'PARTIEL',
  Signé: 'SIGNE',
  Refusé: 'REFUSE',
};

/** Interface → API : statut de livraison. */
export function statutLivraisonVersApi(statut) {
  return STATUTS_LIVRAISON[statut] ?? String(statut ?? '').toUpperCase();
}

/** API → interface : statut de livraison. */
export function statutLivraisonVersInterface(statut) {
  const entree = Object.entries(STATUTS_LIVRAISON).find(([, api]) => api === statut);
  return entree ? entree[0] : statut;
}

/** API → interface : bon de livraison. */
export function dtoVersLivraison(dto) {
  return {
    id: dto.id,
    num: dto.numero,
    clientId: dto.clientId,
    clientNom: dto.clientNom ?? '',
    commandeId: dto.orderId,
    date: isoVersDate(dto.dateLivraison),
    statut: statutLivraisonVersInterface(dto.statut),
    adr: dto.adresse ?? '',
    // L'ecran range ici la reference de la facture produite, « F-2026-007 »,
    // et se contente de sa presence quand il n'y en a pas. Reduite a un
    // booleen, elle valait faux : le bon signe se representait comme restant
    // a facturer, et la marchandise partait deux fois en facture.
    facture: dto.factureRef || dto.facture === true,
    sig: dto.signature ?? '',
    signePar: dto.signataire ?? '',
    source: dto.source ?? 'interne',
    lignes: (dto.lignes ?? []).map(dtoVersLigneLogistique),
    total: dto.montantTotal ?? 0,
    livreLe: isoVersDate(dto.livreLe),
    livrePar: dto.livrePar ?? '',
    signeLe: isoVersDate(dto.signeLe),
    factureEnvoyee: dto.factureEnvoyee === true,
  };
}

/** Interface → API : bon de livraison. */
export function livraisonVersDto(b) {
  return {
    id: typeof b.id === 'number' ? b.id : null,
    numero: b.num ?? (typeof b.id === 'string' ? b.id : null),
    clientId: nombre(b.clientId),
    orderId: nombre(b.commandeId),
    dateLivraison: dateVersIso(b.date),
    statut: b.statut ? statutLivraisonVersApi(b.statut) : null,
    adresse: b.adr ?? null,
    facture: !!b.facture,
    factureRef: typeof b.facture === 'string' ? b.facture : null,
    signature: b.sig ?? null,
    signataire: b.signePar ?? null,
    source: b.source ?? 'interne',
    lignes: (b.lignes ?? []).map(ligneLogistiqueVersDto),
    livreLe: dateVersIso(b.livreLe),
    livrePar: b.livrePar || null,
    signeLe: dateVersIso(b.signeLe),
    factureEnvoyee: b.factureEnvoyee === true,
  };
}

// ── Rendez-vous ────────────────────────────────────────────────────────────

/** API → interface : rendez-vous. */
export function dtoVersRendezVous(dto) {
  return {
    id: dto.id,
    // L'ecran des rendez-vous dit « objet » et « lieu », celui de l'ouvrier
    // les relit sous ces noms-la. Le serveur dit « titre » et « adresse ».
    // Les deux vocabulaires sont rendus, le temps qu'ils se rejoignent.
    titre: dto.titre,
    objet: dto.titre ?? '',
    clientId: dto.clientId,
    clientNom: dto.clientNom ?? '',
    chantierId: dto.siteId,
    date: dto.dateRdv,
    heure: dto.heure ?? '',
    adr: dto.adresse ?? '',
    lieu: dto.adresse ?? '',
    notes: dto.notes ?? '',
    statut: dto.statut ?? 'PLANIFIE',
    type: dto.typeRdv ?? 'autre',
    // Sans eux, l'ouvrier ne voit jamais le rendez-vous auquel on l'a
    // convoque — ce que l'ecran lui promet pourtant sous le bouton.
    employeIds: dto.convoques ?? [],
    heureFin: dto.heureFin ?? '',
    avecQui: dto.avecQui ?? '',
    lieuNom: dto.lieuNom ?? '',
    noteEquipe: dto.noteEquipe ?? '',
    // Pour le bureau : qui a vu, qui vient, qui ne peut pas.
    reponses: dto.reponses ?? [],
    // Pour l'employé : sa propre réponse, et le signal pour y répondre.
    maReponse: dto.maReponse ?? null,
  };
}

/** Interface → API : rendez-vous. */
export function rendezVousVersDto(r) {
  return {
    id: typeof r.id === 'number' ? r.id : null,
    titre: r.objet ?? r.titre ?? '',
    clientId: nombre(r.clientId),
    siteId: nombre(r.chantierId),
    dateRdv: dateVersIso(r.date),
    heure: r.heure || null,
    adresse: r.lieu ?? r.adr ?? null,
    notes: r.notes ?? null,
    statut: r.statut ?? null,
    typeRdv: r.type ?? 'autre',
    // Un employe encore local porte un identifiant de travail, que la base
    // refuserait : seuls ceux venus du serveur sont transmis.
    convoques: (r.employeIds ?? []).filter((id) => typeof id === 'number'),
    // Sans eux, enregistrer le rendez-vous depuis l'agenda effaçait l'heure
    // de fin, le lieu tapé à la main et la note pour l'équipe.
    heureFin: r.heureFin || null,
    avecQui: r.avecQui || null,
    lieuNom: r.lieuNom || null,
    noteEquipe: r.noteEquipe || null,
  };
}

// ── Catalogue de prestations ───────────────────────────────────────────────

/** API → interface : prestation du catalogue. */
export function dtoVersPrestation(dto) {
  return {
    id: dto.code,
    backendId: dto.id,
    metier: dto.metier ?? '',
    nom: dto.nom,
    u: dto.unite ?? 'forfait',
    p: dto.prix ?? 0,
    sd: dto.sousDesignation ?? '',
    // Numero de position CAN/NPK : le lecteur de soumissions reconnait la
    // prestation d'un poste par lui avant de comparer les noms.
    codeCan: dto.codeCan ?? '',
  };
}

/** Interface → API : prestation du catalogue. */
export function prestationVersDto(a) {
  return {
    id: typeof a.backendId === 'number' ? a.backendId : null,
    code: String(a.id ?? ''),
    nom: a.nom ?? '',
    metier: a.metier ?? null,
    unite: a.u ?? 'forfait',
    prix: nombre(a.p) ?? 0,
    sousDesignation: a.sd ?? null,
    codeCan: a.codeCan || null,
  };
}

// ── Journal des connexions ─────────────────────────────────────────────────

/** API → interface : entrée du journal des connexions. */
export function dtoVersConnexion(dto) {
  const date = dto.dateConnexion ? new Date(dto.dateConnexion) : null;
  return {
    id: dto.id,
    nom: dto.nomUtilisateur,
    role: dto.roleUtilisateur,
    jour: date
      ? date.toLocaleDateString(localeDates(), { weekday: 'long', day: '2-digit', month: 'long' })
      : '',
    heure: date ? date.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' }) : '',
  };
}

/** Interface → API : entrée du journal des connexions. */
export function connexionVersDto(c) {
  return {
    nomUtilisateur: c.nom ?? '',
    roleUtilisateur: c.role ?? '',
  };
}

// ── Feuilles de régie ──────────────────────────────────────────────────────

/** API → interface : feuille de régie. */
export function dtoVersFeuille(dto) {
  return {
    id: dto.id,
    chId: dto.siteId,
    chNom: dto.chantierNom ?? '—',
    empId: dto.employeeId,
    date: isoVersDate(dto.dateFeuille),
    empNom: dto.auteur ?? '',
    // Le magasinier qui l'a remplie pour un collègue : il la retrouve ainsi.
    saisiParId: dto.saisiParId ?? null,
    par: dto.modeSaisie ?? '',
    adr: dto.adresse ?? '',
    deb: dto.heureDebut ?? '',
    fin: dto.heureFin ?? '',
    pause: dto.pauseMinutes ?? 0,
    heures: dto.heures ?? 0,
    travaux: dto.travaux ?? '',
    lignes: (dto.lignes ?? []).map((l) => ({
      id: l.id,
      d: l.designation,
      ref: l.referenceArticle ?? undefined,
      u: l.unite ?? '',
      q: l.quantite ?? 0,
      p: l.prixUnitaire ?? 0,
      // L'espace ouvrier lit le materiel sous ces noms-la.
      nom: l.designation,
      qte: l.quantite ?? 0,
    })),
  };
}

/** Interface → API : feuille de régie. */
export function feuilleVersDto(f) {
  return {
    id: typeof f.id === 'number' ? f.id : null,
    siteId: nombre(f.chId),
    employeeId: nombre(f.empId),
    dateFeuille: dateVersIso(f.date),
    auteur: f.empNom ?? null,
    modeSaisie: f.par ?? null,
    adresse: f.adr ?? null,
    heureDebut: f.deb || null,
    heureFin: f.fin || null,
    pauseMinutes: nombre(f.pause) ?? 0,
    heures: nombre(f.heures) ?? 0,
    travaux: f.travaux ?? null,
    lignes: (f.lignes ?? []).map((l, i) => ({
      id: typeof l.id === 'number' ? l.id : null,
      position: i,
      // L'espace ouvrier note le materiel sous « nom » et « qte » : lire
      // seulement « d » envoyait une designation vide, que le serveur refuse,
      // et la feuille de regie disparaissait avec tout son materiel.
      designation: l.d ?? l.nom ?? '',
      referenceArticle: l.ref ?? null,
      unite: l.u ?? null,
      quantite: nombre(l.q ?? l.qte) ?? 0,
      prixUnitaire: nombre(l.p) ?? 0,
    })),
  };
}

// ── Messages employés ──────────────────────────────────────────────────────

/** API → interface : message d'employé. */
export function dtoVersMessageEmploye(dto) {
  return {
    id: dto.id,
    de: dto.auteur ?? 'employe',
    texte: dto.texte ?? '',
    date: dto.dateMessage ?? '',
    lu: dto.lu === true,
  };
}

/** Interface → API : message d'employé. */
export function messageEmployeVersDto(m) {
  return {
    id: typeof m.id === 'number' ? m.id : null,
    auteur: m.de ?? 'employe',
    texte: m.texte ?? '',
    dateMessage: m.date && String(m.date).includes('T') ? m.date : null,
    lu: m.lu === true,
  };
}
